/**
 * Canonical v1 API contract types — SPA architectural convergence plan §2.
 *
 * These types are the source of truth for the SPA's view of the
 * v1 contract. The corresponding Pydantic models live in
 * `backend/api_server/schemas/v1.py`; both are hand-maintained
 * against this same definition. Drift between the two must be
 * resolved by editing both files together.
 *
 * Do NOT hand-mirror these from upstream Open-RMF types. The v1
 * contract is Nexsoss-authored; upstream types in
 * `src/api/generated/` are a frozen diagnostic/oracle reference only
 * (convergence plan §Locked architecture: "Upstream rmf-web and
 * rmf-api-server remain diagnostic/oracle components only; do not
 * fork or add them to the production path.").
 *
 * Field naming: snake_case to match the JSON wire format from
 * `nexsoss-api-server`. Optional fields use `?`. Discriminated
 * unions use a `kind` literal field.
 */

// =============================================================================
// Envelope — every projected entity and event embeds these metadata fields
// (convergence plan §Live consistency protocol).
// =============================================================================

/**
 * Wallclock timestamp (ms since epoch) at which the per-site worker
 * received the value from RMF. Distinct from `source_timestamp_ms`
 * (when RMF itself recorded the value).
 */
export interface ProjectorReceiveTimestamp {
  readonly projector_received_at_ms: number;
}

/**
 * Source-side timestamp the RMF core attached to the message.
 */
export interface SourceTimestamp {
  readonly source_timestamp_ms: number;
}

/**
 * Monotonic projector epoch for the issuing worker. Increments on
 * worker restart; the SPA replaces an entire category from an exact
 * snapshot when the epoch changes.
 */
export interface ProjectorEpoch {
  readonly projector_epoch: number;
}

/**
 * Category revision and (for live events) per-category sequence.
 * The SPA replaces an entire category from an exact snapshot when
 * it observes a sequence gap.
 */
export interface CategoryRevision {
  readonly category_revision: number;
  readonly category_sequence?: number;
}

/**
 * Freshness in ms — `now - source_timestamp_ms`. The SPA uses
 * this to drive stale-state indicators and to disable
 * safety-relevant controls when the freshness exceeds the
 * capability's tolerance.
 */
export interface Freshness {
  readonly freshness_ms: number;
}

/**
 * v1 schema version. Bumped on any breaking change to the
 * envelope or entity shape. The SPA refuses to render a payload
 * whose `schema_version` it does not understand.
 */
export interface SchemaVersion {
  readonly schema_version: '1.0';
}

/**
 * Combined envelope prefix attached to every projected entity and event.
 */
export interface V1Envelope
  extends
    SourceTimestamp,
    ProjectorReceiveTimestamp,
    ProjectorEpoch,
    CategoryRevision,
    Freshness,
    SchemaVersion {
  readonly site: string;
}

// =============================================================================
// IDs and identifiers — opaque, immutable, site-scoped (plan §2.4).
// =============================================================================

/**
 * Immutable opaque identifier for a site. Site IDs are minted at
 * deployment-manifest time (plan §Locked architecture §4) and never
 * re-used.
 */
export type SiteId = string & { readonly __brand: 'SiteId' };

/**
 * Composite robot identity — `(site, fleet, robot)`. The three
 * segments are opaque; the tuple is the only safe way to refer to
 * a specific robot across multi-site data.
 */
export interface RobotId {
  readonly site: SiteId;
  readonly fleet: string;
  readonly robot: string;
}

/**
 * Identifier for a fleet. Site-scoped.
 */
export interface FleetId {
  readonly site: SiteId;
  readonly fleet: string;
}

/**
 * Identifier for a door.
 */
export interface DoorId {
  readonly site: SiteId;
  readonly door: string;
}

/**
 * Identifier for a lift.
 */
export interface LiftId {
  readonly site: SiteId;
  readonly lift: string;
}

/**
 * Identifier for a task. Assigned by `nexsoss-api-server` at
 * intent-receipt time.
 */
export interface TaskId {
  readonly site: SiteId;
  readonly task: string;
}

// =============================================================================
// Capabilities (plan §Safety and authorization)
// =============================================================================

/**
 * Stable capability IDs returned by the API. The SPA gates UI on
 * these; the API enforces them authoritatively.
 */
export type CapabilityId =
  | 'task.dispatch'
  | 'task.cancel'
  | 'task.interrupt'
  | 'task.kill'
  | 'task.resume'
  | 'task.rewind'
  | 'task.skip_phase'
  | 'task.undo_phase'
  | 'robot.commission'
  | 'robot.decommission'
  /**
   * Automatic charging — RMF's per-robot `idle_behavior` commission
   * flag — on or off. Deliberately NOT folded into
   * `robot.commission`: the only console control that could stop a
   * robot driving itself back to its charger was Decommission, which
   * also stops it bidding for work, so an engineer tending a robot
   * had to take it out of service to keep it still. This verb flips
   * that one flag and nothing else.
   *
   * Mirrors `ROBOT_SET_IDLE_BEHAVIOR` in the backend's
   * `policy/capabilities.py`, which is authoritative; this union only
   * decides whether the control is drawn.
   */
  | 'robot.set_idle_behavior'
  | 'robot.direct_action'
  | 'robot.release_mutex'
  /**
   * The dustbag acknowledgement -- "the bag is empty, carry on".
   *
   * The one `robot.*` capability granted MORE widely than
   * `robot.commission`'s tier (service_operator / foh_operator
   * upward), and deliberately: it ends a wait, it starts no motion.
   * The robot has driven itself to a DustBagStation and parks there
   * (30 minutes by default) waiting to be told the bag was emptied, and
   * the person holding the bin liner is a service or FOH operator.
   * Withholding the ack from them buys no safety and strands a robot
   * in a corridor until somebody senior is free.
   *
   * Mirrors `ROBOT_CONFIRM_DUSTBAG` in the backend's
   * `policy/capabilities.py`, which is authoritative.
   */
  | 'robot.confirm_dustbag'
  | 'robot.fire_relocalize'
  | 'door.open'
  | 'door.close'
  /**
   * THE MANUAL-OPEN OVERRIDE, and NOT a door command: it does not ask
   * the door to do anything.
   *
   * It writes a record in the site's lifecycle broker that makes
   * `rmf-door-state-mux` suppress the door adapter's raw states and
   * synthesize MODE_OPEN on its own 1 s timer -- which is what
   * releases a robot standing at a door the pinned RMF will otherwise
   * wait at FOREVER (its DoorOpen phase has no timeout; delay merely
   * accrues and nothing alerts). Because the mux synthesizes rather
   * than relays, it works with the door adapter dead, which is the
   * case it exists for: `L4_SLL_Door`'s MEGAZO adapter could not log
   * in to its vendor cloud and published nothing at all.
   *
   * It carries an ATTESTATION because the operator is asserting an
   * observation the platform cannot make. That is why it sits with
   * the supervisor rather than with the device operators who hold
   * `door.open`/`door.close`: those two ask a door to move and are
   * answered by the door; this one is a promise about the physical
   * world, and declaring a door open that is NOT open makes RMF drive
   * a robot at it.
   *
   * Mirrors `DOOR_DECLARE_OPEN`/`DOOR_REVOKE_OVERRIDE` in the
   * backend's `policy/capabilities.py`, which is authoritative; this
   * union and `DOOR_OVERRIDE_ROLES` in `auth/types.ts` only decide
   * whether the control is drawn.
   */
  | 'door.declare_open'
  | 'door.revoke_override'
  /** DEPRECATED (spa 0.5.10): `request_destination` is retired; the
   *  api-server answers 410 and the worker publishes nothing. The
   *  operator's floor request is `lift.manual_call` below. */
  | 'lift.request_destination'
  /**
   * The operator's own floor request (`POST /lifts/{lift}/call`) and its
   * cancel (`/close`). NOT `request_destination`: that verb went to
   * rmf_lift_supervisor under a `nexsoss-worker-*` id the supervisor
   * re-presented at 1 Hz forever, which is the loop that took Hotel
   * NoMad's lifts down on 2026-08-29. A manual call is an OPERATOR-owned
   * one-shot session at the lift-session adapter: it never enters the
   * supervisor, is never retried, and answers with a per-request verdict
   * (`LiftSessionResult`) instead of a CommandRecord frozen at "accepted".
   */
  | 'lift.manual_call'
  | 'lift.manual_close'
  | 'lift.acquire_session'
  | 'lift.release_session'
  | 'lift.set_mode'
  /**
   * Tier 2 of the lift write gate: arming (and disarming) the site's
   * lift bridge so it will accept DISPATCHING writes at all. Held by
   * supervisors and platform admins ONLY -- deliberately narrower than
   * `lift.request_destination`, which every operator role holds.
   *
   * The asymmetry is the point. Requesting a floor moves one lift once;
   * arming puts an entire guest lift bank into AGV mode and nothing at
   * any layer caps how many robots then call it, so at Hotel NoMad an
   * armed bridge is six cars a fleet can take at will. Mirrors
   * `LIFT_ARM_WRITES` in the backend's `policy/capabilities.py`, which
   * is authoritative; this and `LIFT_ARM_WRITES_ROLES` in
   * `auth/types.ts` only decide whether the control is drawn.
   */
  | 'lift.arm_writes'
  /**
   * Block a lift from robot use, or travel between floors on it, by hand, and
   * reset a tripped lift. Supervisors and platform admins (Akash, 2026-11-02):
   * the front desk sees the state and no buttons. Mirrors `LIFT_RULE_SWITCH`
   * in `policy/capabilities.py`, which is authoritative.
   */
  | 'lift.rule_switch'
  /** The same pair, for lift rules on the calendar. */
  | 'lift.rule_calendar'
  | 'schedule.manage'
  | 'emergency.trigger'
  | 'emergency.reset'
  /**
   * Planned maintenance windows. Held by supervisors and platform
   * admins only, NOT by `foh_operator` who holds `schedule.manage` — a
   * window halts the whole site's dispatch with no override, which is
   * closer to raising fire mode than to booking a clean.
   */
  | 'maintenance.manage';

export type CapabilityDisabledReason =
  | 'unsupported_by_rmf'
  | 'unsupported_by_worker'
  | 'site_assignment_missing'
  | 'role_policy_denies'
  | 'deployment_allowlist_excludes'
  | 'worker_unreachable'
  | 'worker_not_ready'
  | 'stale_state'
  | 'database_unavailable'
  | 'maintenance_mode';

export type WorkerStatus = 'unreachable' | 'initialising' | 'ready' | 'stale' | 'failed';

export interface EffectiveCapability {
  readonly capability: CapabilityId;
  readonly enabled: boolean;
  readonly disabled_reasons: CapabilityDisabledReason[];
}

export interface SiteCapabilities {
  readonly site: SiteId;
  readonly capabilities: EffectiveCapability[];
  /** Effective evaluation timestamp — drives cache invalidation. */
  readonly evaluated_at_ms: number;
}

// =============================================================================
// Sites
// =============================================================================

export interface SiteSummary {
  readonly site: SiteId;
  readonly label: string;
  readonly deployment_manifest_version: string;
  readonly certified_capabilities: CapabilityId[];
  readonly worker_status: 'unreachable' | 'initialising' | 'ready' | 'stale' | 'failed';
  readonly worker_epoch?: number;
  /**
   * The IANA zone the SITE keeps — set at commission, stored on the
   * `sites` row. NOT the browser's zone: anything that renders or
   * writes a wall clock for this site must use this one, because the
   * operator's clock and the building's are routinely different and a
   * schedule written in the wrong one runs hours off and follows the
   * wrong DST calendar. Optional in TypeScript only because a server
   * predating this field (or the mock server) omits it; treat a missing
   * value as unknown and say so rather than silently substituting the
   * browser's.
   */
  readonly timezone?: string;
}

export interface SiteDetail extends SiteSummary {
  readonly building_map_revision?: string;
  readonly last_projection_at_ms?: number;
  readonly manifest_revisions: Record<string, number>;
}

// =============================================================================
// Fleet and robot registry (static; never overwritten by state-only updates)
// =============================================================================

export type RobotCommissionState = 'commissioned' | 'decommissioned' | 'unknown';

export interface RobotRegistry {
  readonly id: RobotId;
  readonly commission_state: RobotCommissionState;
  readonly issues: string[];
  readonly mutex_groups: string[];
  readonly supported_direct_actions: CapabilityId[];
  readonly last_certified_at_ms: number;
  /**
   * The three raw commission booleans exactly as the fleet adapter
   * last published them, carried BESIDE the settled tri-state rather
   * than replacing it.
   *
   * `commission_state` collapses `(true, true, false)` to `unknown`,
   * and it has to keep doing so: fire mode's enforcement sweep tests
   * convergence by looking for `decommissioned`, so a derivation that
   * called that combination "commissioned" would let a robot with
   * automatic charging off drive itself around during an evacuation.
   * The raw flags are what let the console say "automatic charging is
   * off" about a robot that is otherwise fully in service.
   *
   * Optional because a worker older than 0.4.11 publishes no such
   * object at all; each flag is null when the publisher omitted it.
   */
  readonly commission?: {
    readonly dispatch_tasks: boolean | null;
    readonly direct_tasks: boolean | null;
    readonly idle_behavior: boolean | null;
  } | null;
}

/**
 * Live robot state. The registry fields above are static and never
 * appear here.
 */
export interface RobotState {
  readonly id: RobotId;
  readonly status:
    | 'idle'
    | 'charging'
    | 'active'
    | 'error'
    | 'offline'
    /** Configured and reachable, but not placeable on the nav graph --
     *  needs re-localizing. See `unavailable_reason`. */
    | 'pending-localize';
  /** Why the robot is not usable, when status is `pending-localize` or
   *  `offline` and the adapter told us: `not_localized`,
   *  `location_status_absent`, `pose_not_on_graph`, `edge_offline`,
   *  `not_reporting`. Absent for a healthy robot. */
  readonly unavailable_reason?: string;
  /** Battery percent normalised to 0..100 (centralised at projection ingress). */
  readonly battery_percent: number;
  /** Current location in the site frame. */
  readonly location: {
    readonly map: string;
    readonly x: number;
    readonly y: number;
    readonly yaw: number;
  };
  /** Current task, if any. */
  readonly task_id?: TaskId;
  readonly updated_at_ms: number;
}

export interface FleetRegistry {
  readonly id: FleetId;
  readonly robots: RobotRegistry[];
}

// =============================================================================
// Doors (registry + state — registry never overwritten by state)
// =============================================================================

export type DoorMode = 'open' | 'closed' | 'moving' | 'unknown';

export interface DoorRegistry {
  readonly id: DoorId;
  readonly level: string;
  readonly kind: 'single' | 'double';
  readonly v1_x: number;
  readonly v1_y: number;
  readonly v1_yaw: number;
  readonly door_width_m: number;
  readonly supported_actions: Array<'open' | 'close'>;
}

/** Who asked: an operator through the console, a fleet adapter for a
 *  robot (`<fleet>/<robot>`), or anything else on the RMF request topic. */
export type DoorCommandSource = 'console' | 'robot' | 'other';

export interface DoorLastCommand {
  readonly source: DoorCommandSource;
  /** Operator display name, RMF requester id, or the raw requester. Never empty. */
  readonly actor: string;
  readonly requested: 'open' | 'close';
  /** When the request was FIRST made; RMF re-asks every second and the
   *  worker keeps the original stamp across those repeats. */
  readonly at_ms: number;
}

export interface DoorState {
  readonly id: DoorId;
  readonly mode: DoorMode;
  readonly updated_at_ms: number;
  /** The most recent door request the worker saw, whoever sent it.
   *  Absent or null until one has been seen (and from workers older
   *  than 0.4.21). */
  readonly last_command?: DoorLastCommand | null;
}

// =============================================================================
// Lifts (registry + state; server-owned sessions; plan §Command lifecycle)
// =============================================================================

export type LiftMode = 'agv' | 'human' | 'off' | 'fire' | 'unknown';
export type LiftDoorState = 'open' | 'closed' | 'moving' | 'unknown';
export type LiftMotionState = 'stopped' | 'moving' | 'unknown';

/** One of the lift cabin's own landing doors (`Lift.doors[]`
 *  upstream) — real geometry, not a nearby-but-unrelated `DoorRegistry`
 *  entry on the level the lift happens to sit on. */
export interface LiftDoorGeometry {
  readonly v1_x: number;
  readonly v1_y: number;
  readonly v1_yaw: number;
  readonly door_width_m: number;
  readonly kind: 'single' | 'double';
}

/** A named, dispatchable waypoint (plan §Phase 4D location
 *  autocomplete) — sourced from the building map's nav-graph vertices
 *  and place list, merged and deduped by name in the worker. */
/**
 * One dispatchable task category, with the places it may target.
 *
 * An INTERSECTION, not a declaration. A category appears here only
 * when the site's fleet adapters accept it AND the worker can build
 * the request AND the building map offers places for it. Each is
 * necessary and none is sufficient — hotel1's fleets declare
 * `delivery: true` while its world has no dispensers or ingestors, so
 * delivery is correctly absent there and present at office1, which has
 * both.
 *
 * So an EMPTY array is a real answer — "nothing is dispatchable at
 * this site right now" — and not a failure to load.
 *
 * The operand lists are role-named because the roles are not
 * interchangeable: a delivery's pickup needs a dispenser and its
 * dropoff an ingestor; a clean needs a cleaning zone. Only the lists a
 * category uses are populated, mirroring the dispatch form's own
 * per-category fields.
 */
export interface TaskCatalogEntry {
  readonly category: string;
  readonly label: string;
  /**
   * patrol / station / dustbag.
   *
   * For `dustbag` these are the site's DustBagStations, and they reach
   * this list by a different route from the others: a DustBagStation
   * is an ordinary holding point with no distinguishing map param, so
   * there is nothing to derive it from. The site DECLARES the names
   * (`sites/<id>.yml` -> `NEXSOSS_DUSTBAG_STATIONS`, the same plumbing
   * as the category list) and the worker intersects them with the live
   * nav-graph places -- which keeps the property that matters: a name
   * the graph does not have never reaches the operator's selector, so
   * a typo in the descriptor drops an option instead of dispatching a
   * task to a place no fleet can reach. The SPA must therefore read
   * this list rather than naming stations itself.
   */
  readonly places?: readonly string[];
  /** clean. */
  readonly zones?: readonly string[];
  /** delivery. */
  readonly pickup_places?: readonly string[];
  readonly dropoff_places?: readonly string[];
  /**
   * dustbag only: the site's default wait at the station, in seconds —
   * what a dispatch with no wait of its own gets. The api-server fills
   * it in from the worker's `site_defaults`, published beside the
   * entries so neither side has to ship first. Absent or null (a worker
   * or api-server older than the field) means the platform's 30 minutes.
   */
  readonly dustbag_timeout_s?: number | null;
  /**
   * clean only: the clean modes this site offers (`vacuum` | `sweep` |
   * `dust_mop` | `scrub`; an older worker may say `wet_scrub`, read as
   * `scrub`), in the site's order -- one dispatch-form choice each. Filled
   * in by the api-server from the worker's `site_defaults`, by the
   * dustbag wait's route. Absent or null (an older worker or api-server,
   * or a site that sets nothing) means vacuum only.
   */
  readonly modes?: readonly string[] | null;
  /**
   * clean only: the site's estimated clean time per zone, in seconds --
   * what RMF is told the clean takes, for robots whose own API gives no
   * estimate. Absent or null: the site has no table; nothing is shown.
   */
  readonly zone_estimates_s?: Readonly<Record<string, number>> | null;
  /**
   * Every entry: what each priority level does at this site's robots,
   * level -> effect (`{ normal: 'planned', high: 'ahead' }` from worker
   * 0.4.40). `planned`: fitted in with the other waiting jobs in the order
   * that gets them all done soonest; `ahead`: goes ahead of the waiting
   * Normal jobs, without stopping the one under way. Filled in by the
   * api-server from the worker's `site_defaults`. Absent or null (a worker
   * that does not send priority, such as 0.4.39) means the robots do not
   * act on it, and the form says so.
   */
  readonly priority_effects?: Readonly<Record<string, string>> | null;
}

export interface PlaceRegistry {
  readonly name: string;
  readonly level: string;
  readonly x: number;
  readonly y: number;
  /**
   * Running fleets whose navigation graph contains this waypoint.
   * Always non-empty — the worker derives this registry from
   * `/nav_graphs`, which only RUNNING fleet adapters publish, so a
   * place with no owning fleet is never emitted. Optional in the type
   * only to tolerate a worker older than this field.
   */
  readonly fleets?: readonly string[];
  /**
   * Map annotations that say what this place can DO, carried through
   * from the map author's `building.yaml` via
   * `rmf_building_map_msgs/GraphNode.params`.
   *
   * These are what make a task category EXECUTABLE here, which is a
   * different question from whether a fleet is configured to accept it
   * — hotel1's fleets declare `delivery: true` while no vertex in that
   * world carries `pickup_dispenser`/`dropoff_ingestor`, so a delivery
   * there wins an auction and then has no workcell to transfer with.
   *
   * The handlers are NAMES, not booleans: a delivery request has to
   * address its dispenser and ingestor by name.
   *
   * All optional in the type only, to tolerate a worker older than
   * these fields — an absent annotation reads as "cannot", which is
   * the safe direction.
   */
  readonly is_cleaning_zone?: boolean;
  readonly pickup_dispenser?: string | null;
  readonly dropoff_ingestor?: string | null;
}

export interface LiftRegistry {
  readonly id: LiftId;
  readonly level: string;
  readonly levels: string[];
  readonly v1_x: number;
  readonly v1_y: number;
  readonly v1_yaw: number;
  readonly door_width_m: number;
  readonly door_height_m: number;
  readonly doors: LiftDoorGeometry[];
  readonly supported_actions: Array<'request_destination' | 'set_mode'>;
}

// -----------------------------------------------------------------------------
// The lift-session adapter's view of a car (adapters-lift
// `contracts/nexsoss/lift_session_state.schema.json`, 1.1), merged
// verbatim by the worker into `LiftState.session`. Written by hand to
// match the contract -- `src/api/generated/` is regenerated from the
// api-server's OpenAPI by a separate step and must not be edited here.
// -----------------------------------------------------------------------------

export type LiftSessionOwnerKind = 'robot' | 'operator';

/** The ADAPTER's belief about who holds the car (its arbiter), not the
 *  connector's echo. Robot ids are `<fleet>/<robot>` -- the same string
 *  the supervisor knows as the RMF session id, which is why "End RMF
 *  session" keys off `owner.id`. Operator ids are the actor who pressed. */
export interface LiftSessionOwner {
  readonly kind: LiftSessionOwnerKind;
  readonly id: string;
}

export type LiftSessionWaitKind = 'ack' | 'telemetry' | 'agv_mode' | 'clearance';

/** The one step the edge is waiting on. `advance_eligible` is the ONLY
 *  thing the Release button may key on -- false for `agv_mode` always,
 *  because no operator can confirm that the car granted AGV control.
 *  Release used to be enabled on any non-empty phase; pressing it on an
 *  agv_mode wait was refused NOT_ADVANCEABLE, which the adapter then
 *  recorded as the LIFT's standing refusal and closed the lanes. */
export interface LiftSessionWait {
  readonly name: string;
  readonly kind: LiftSessionWaitKind;
  readonly advance_eligible: boolean;
  /** Edge-monotonic on edge <= 0.7.3. Never render it. */
  readonly deadline_ts?: number | null;
  readonly started_ts?: number | null;
  /** Epoch seconds, connector-derived on first sight of the wait (over by
   *  up to one state period). The countdown comes from THIS. */
  readonly deadline_epoch_ts?: number | null;
  readonly started_epoch_ts?: number | null;
}

/** LIFT-WIDE standing refusal produced by RIDE verbs (open/goto/close).
 *  Drives lane closure and alerts. Never written by an operator verb --
 *  that is `LiftSessionResult`, and the two must not be confused. */
export interface LiftSessionRefusal {
  readonly code: string;
  readonly permanent: boolean;
  readonly reason: string;
  /** Moves on every repeat of the refusal; the adapter's 60 s self-clear
   *  compares against it. Never render it as "since". */
  readonly since_ts: number;
  readonly consecutive?: number;
  readonly escalated?: boolean;
  /** K1 (lift-session 0.1.18): adapter wall-clock epoch seconds at which
   *  this code was FIRST seen, carried forward while the same code
   *  repeats. Absent or null before 0.1.18. */
  readonly first_since_epoch_ts?: number | null;
}

export type LiftOperatorVerb =
  | 'advance'
  | 'recover'
  | 'end_rmf_session'
  | 'manual_call'
  | 'manual_close';

export type LiftSessionResultStatus = 'done' | 'refused' | 'failed' | 'timeout';

/** The per-request verdict of the last OPERATOR verb on this lift, keyed
 *  by the api-server-minted `request_id` the press was answered with.
 *  This is the ONLY verdict path: the CommandRecord stays at "accepted"
 *  forever, because admission at the adapter is not the outcome. */
export interface LiftSessionResult {
  readonly request_id: string;
  readonly verb: string;
  readonly actor?: string | null;
  readonly status: LiftSessionResultStatus;
  readonly code?: string | null;
  readonly permanent?: boolean | null;
  readonly reason: string;
  /** Epoch seconds. Folded into the SPA's idempotency keys so a re-press
   *  after a verdict is a NEW request rather than a replay of the old
   *  record (`useOperatorRequest.ts::operatorIdempotencyKey`). */
  readonly ts: number;
}

export interface LiftSessionInFlight {
  readonly verb: string;
  readonly request_id: string;
  readonly operator_request_id?: string | null;
  readonly issued_ts?: number;
  readonly deadline_ts?: number;
}

export interface LiftSessionView {
  readonly active: boolean;
  /** The edge's phase string, unchanged (idle, requesting_agv, entry_ack,
   *  moving, held_boarding, held, held_egress, releasing, ...). */
  readonly phase: string | null;
  readonly phase_ts?: number | null;
  /** The EDGE ride id (`open-<hex>`) -- not an operator request id and
   *  not an RMF session id. */
  readonly request_id?: string | null;
  readonly owner: LiftSessionOwner | null;
  readonly wait: LiftSessionWait | null;
  readonly stalled: boolean;
  readonly awaiting_clearance: boolean;
  readonly heartbeat_age?: number | null;
  /** Non-empty means a press would be refused IN_FLIGHT (except
   *  manual_close, which is always admitted). */
  readonly in_flight: readonly LiftSessionInFlight[];
  readonly retry_after_s: number;
  readonly lanes_closed: boolean;
  readonly refusal: LiftSessionRefusal | null;
  readonly last_result: LiftSessionResult | null;
  /** OPERATOR-owned sessions only: epoch seconds at which the adapter
   *  closes the session by itself if nobody presses Cancel call. A car
   *  must never stay held with nobody asking. Null otherwise. */
  readonly auto_close_epoch_ts?: number | null;
  /** The connector's `telemetry.mode_label` when the adapter carries it
   *  through (additive; `session` allows extra properties). Rendered on
   *  the agv_mode wait line so an operator sees what the car says. */
  readonly mode_label?: string | null;
  // K1 (twin slice 1; lift-session 0.1.18). All optional and nullable:
  // absent from an older adapter, and null means "not reported", the
  // same as absent. They live inside `session` only: the adapter
  // document's root (`additionalProperties: false`) and the api-server's
  // `LiftState` (`extra=forbid`) both refuse unknown keys.
  /** Why the lanes are closed: the adapter's closure code, which is not
   *  always `refusal.code`. */
  readonly lanes_closed_code?: string | null;
  /** Adapter wall-clock epoch seconds at which the CURRENT closure
   *  started; kept across a code change. Show as "since HH:MM", never as
   *  a countdown (another machine's clock). */
  readonly lanes_closed_since_epoch_ts?: number | null;
  /** The connector's `telemetry.commandable`: whether the car can take a
   *  robot call. */
  readonly commandable?: boolean | null;
  /** The connector's `telemetry.fresh`. False marks the car lost. */
  readonly telemetry_fresh?: boolean | null;
  /** Seconds since the adapter last received the connector's document,
   *  on the adapter's monotonic clock. 30 or more marks the car lost. */
  readonly reading_age_s?: number | null;
}

/** What rmf_lift_supervisor is currently re-presenting on `lift_requests`
 *  for this car. A `nexsoss-worker-*` session id here is a LEGACY
 *  request from the retired `request_destination` path, pending the
 *  worker's own drain -- read-only in the console. */
export interface LiftStandingRequest {
  readonly session_id: string;
  readonly request_type: number;
  readonly destination_floor: string;
  readonly ts: number;
}

export interface LiftState {
  readonly id: LiftId;
  readonly current_level: string;
  readonly destination_level: string | null;
  readonly mode: LiftMode;
  readonly door_state: LiftDoorState;
  readonly motion_state: LiftMotionState;
  /** `motion_state` collapses upstream's MOTION_UP/MOTION_DOWN into
   *  one "moving" value; direction is carried separately here. */
  readonly motion_direction: 'up' | 'down' | null;
  readonly fault: boolean;
  readonly updated_at_ms: number;
  /** Currently active RMF wire session (echoed back by the lift
   *  itself) — may not be ours (a stuck robot/fleet-adapter session,
   *  the oracle dashboard's, etc.). Empty string when unclaimed.
   *  Distinct from the nexsoss-owned `LiftSession` below. */
  readonly rmf_session_id: string;
  /**
   * The lift adapter's session phase, when a ride is running.
   *
   * Not an RMF field -- it arrives from the adapter and is merged into
   * this document so the console reads one per lift. "Release lift"
   * echoes it back so the bridge can refuse a confirmation that no
   * longer describes what is happening.
   *
   * Absent when nothing is running, or when the adapter predates the
   * field; the button then stays disabled rather than sending a phase
   * the SPA invented.
   *
   * Kept for one release (worker 0.4.8 still writes it); `session.phase`
   * is the same string and wins when both are present.
   */
  readonly session_phase?: string | null;
  /**
   * The whole adapter session view (section 4 of the 2026-08-29 landing
   * plan). Absent from a 0.4.7 worker; `null` when the adapter has not
   * published for this lift yet.
   */
  readonly session?: LiftSessionView | null;
  readonly standing_request?: LiftStandingRequest | null;
  /**
   * The lift-session adapter's ACTIVE alerts for this car (worker 0.4.31):
   * a lane closure and why, or a stalled car. Absent from an older worker.
   * Nothing in the console reads it yet; the api-server's `lift_refusal`
   * does (`backend/api_server/alerts_engine.py`).
   */
  readonly adapter_alerts?: readonly LiftAdapterAlert[];
}

/** One relayed lift-session adapter alert (`v1_contract` `LiftAdapterAlert`). */
export interface LiftAdapterAlert {
  readonly alert_id: string;
  readonly code: string;
  readonly severity: string;
  readonly message: string;
  /** Adapter wall clock, epoch seconds: first heard active. */
  readonly first_ts?: number | null;
  /** Adapter wall clock, epoch seconds: the latest repeat. */
  readonly ts?: number | null;
}

/**
 * Server-owned lift session. Expires after `expires_at_ms`. Tied
 * to user, site, lift, and command ID. Disallow arbitrary client
 * session IDs and disable controls when ownership cannot be
 * reconstructed.
 */
export interface LiftSession {
  readonly session_id: string;
  readonly site: SiteId;
  readonly lift: LiftId;
  readonly user_sub: string;
  readonly command_id: string;
  readonly acquired_at_ms: number;
  readonly expires_at_ms: number;
}

/**
 * The lift bridge's own validation verdict, straight off the edge
 * bridge's retained `nexsoss/lift/<site>/site/validation` document.
 * Same vocabulary as the contract's `state`.
 */
export type LiftValidationState = 'confirmed' | 'unconfirmed' | 'unknown';

/**
 * The site's lift write gate, as the console reads it.
 *
 * SITE-WIDE, not per-lift, and that is a safety property rather than a
 * simplification: the edge bridge holds exactly one gate for the whole
 * site (invariant I6), so a per-lift shape here would draw a picture
 * the bridge cannot honour -- an operator would arm "lift 3" and get
 * all six of Hotel NoMad's cars.
 *
 * The four gate fields are the bridge's OWN field names, passed
 * through by the api-server unrenamed. That is deliberate: the bug
 * this whole change exists to fix was one field read twice under two
 * names, where the second reading silently answered `false` forever
 * and published MODE_OFFLINE for every lift on the site while the edge
 * was armed and healthy. A rename here would recreate exactly that
 * seam, one hop further out.
 *
 * TWO TIERS, AND THEY ARE INDEPENDENT:
 *   `writes_permitted` is tier 1, and true exactly when `state` is
 *   'confirmed' -- every configured lift address was found on the real
 *   controller. False refuses EVERY write on the site, RELEASE writes
 *   included.
 *   `writes_armed` is tier 2 -- a person deliberately armed this
 *   bridge. A DISPATCH needs both; a RELEASE needs tier 1 alone, which
 *   is what keeps the unattended 03:00 fail-safe release working while
 *   disarmed.
 * Neither may be inferred from the other. An armed bridge whose LATER
 * inventory failed is a real, publishable state (nothing disarms a
 * bridge when validation lapses), so `writes_armed: true` alongside
 * `state: 'unconfirmed'` must render as "not validated" -- tier 1 is
 * refusing everything regardless.
 *
 * NEVER PERSISTED (invariant I1): a bridge that reboots unattended
 * comes back disarmed, so a console showing a stale "Armed" from
 * before a restart would be actively dangerous. Nothing caches this
 * across a session.
 */
export interface LiftWriteGate {
  readonly site: SiteId;
  // NOTE, and it is load-bearing: an api-server that has not yet
  // grown the read path answers 404 on `GET .../lifts/write_gate`, and
  // the console is built to survive that rather than to assume it away.
  // `fetchLiftWriteGate` turns that one status into a `null` -- a
  // settled, successful reading of an ABSENCE -- which every lift
  // surface renders as the 'unsupported' verdict: "this deployment
  // does not publish the gate", kept distinct from both 'unknown' ("we
  // could not read it") and 'disarmed' ("we read it, and it is off").
  // The degradation is deliberate and correct, but it is not the
  // feature: while a deployment is in it, an operator cannot see arm
  // state from this console at all and must use the edge box's own
  // dashboard.
  readonly state: LiftValidationState;
  readonly writes_permitted: boolean;
  readonly writes_armed: boolean;
  /** The bridge's own sentence about WHY -- '41 items matched
   *  (hash=...)' when confirmed, or the specific OPC item paths that
   *  are missing from the translator inventory when not. Rendered
   *  rather than summarised: "which item" is the only thing that tells
   *  an engineer what to fix. */
  readonly detail: string;
}

/**
 * An arm/disarm decision on its way to the bridge.
 *
 * This is the console-owned half of the `lift_arm` contract document
 * (`adapters-lift/contracts/nexsoss/lift_arm.schema.json`), which
 * travels SPA -> api-server -> worker -> cloud lift adapter -> MQTT
 * and is validated against that one schema on every hop.
 *
 * FOUR OF THE DOCUMENT'S FIELDS ARE NOT HERE, and their absence is
 * enforced -- the api-server's `LiftArmRequest` forbids extras, so a
 * client that sends one gets a 422 rather than having it ignored:
 *   `actor`         minted from the verified token. A browser that
 *                   could name its own actor could sign someone else's
 *                   name to an armed lift bank, since `actor` is what
 *                   the bridge records as `armed_by`.
 *   `request_id`    minted server-side, same rule `lift_sessions.py`
 *                   set for `session_id`. The gate is site-wide, so an
 *                   id a caller chooses is one it can collide with
 *                   another operator's concurrent arm.
 *   `ts`            stamped at the moment the request is authorised.
 *   `schema_version` belongs to the hop that builds the document.
 */
export interface LiftArmRequest {
  /** Absolute, never a toggle: a redelivered QoS-1 command must be
   *  able to land twice without flipping the gate back. */
  readonly armed: boolean;
  /**
   * The two-key turn, and meaningful only when arming (invariants
   * I2/I3). Omitted entirely on a disarm rather than sent as `false` --
   * a disarm during an incident must never be refusable on a missing
   * flag, and a field on the wire is a field some future reader starts
   * checking.
   */
  readonly confirmed?: boolean;
  /** Free text for the audit trail. Optional for the same reason
   *  `confirmed` is absent on a disarm. */
  readonly reason?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
  /**
   * WS8's acknowledgement, and it NEVER REACHES THE WIRE: the api-server
   * keeps both fields out of the worker command target, so
   * `lift_arm.schema.json` stays 1.0 and the edge's own validator is
   * unchanged. They go to the audit row, the log line and the response.
   *
   * Valid only with `armed: false` -- arming is never guarded -- and a
   * server that is still 0.6.34 answers either of them with a 422,
   * which is why the SPA ships AFTER the api-server.
   *
   * INVARIANT I2 HOLDS: a disarm is never REFUSED. It may require ONE
   * acknowledgement, and every acknowledgement path is named in the 409
   * that asks for it.
   */
  readonly override_reason?: string;
  readonly override_acknowledges_stranding?: boolean;
}

/**
 * What the api-server can say immediately: the request was authorised
 * and handed to the worker.
 *
 * `requested_armed`, NOT `armed`, and the console must render it that
 * way. This is not the bridge's gate state -- the bridge answers on
 * its own MQTT ack topic and republishes `site/validation` with the
 * real `writes_armed`. Showing a request as though it were the live
 * flag is exactly the class of bug this change exists to fix.
 */
export interface LiftArmResponse {
  readonly command: CommandRecord;
  readonly request_id: string;
  readonly requested_armed: boolean;
  /**
   * Whether the WS8 disarm guard actually ran. `list_unavailable` and
   * `bypassed_fire_mode` both mean "this disarm went through WITHOUT the
   * lift-routed check" -- the safe direction, and exactly the thing a
   * person would otherwise assume had happened. Absent on an arm.
   */
  readonly guard_status?:
    | 'passed'
    | 'list_unavailable'
    | 'overridden'
    | 'bypassed_fire_mode'
    | 'disabled';
  /** Why the guard could not use the feed, one line each. "Unavailable"
   *  with no cause is what made the 2026-09-07 Loki investigation
   *  expensive. */
  readonly warnings?: string[];
  // NOTE, recorded rather than fixed: this interface has never carried
  // `replayed`, which the server has sent since the idempotent-replay
  // fix. Widening it beyond what this release adds is out of scope, and
  // narrowing it is worse; do not "tidy" it into something the server
  // does not send.
}

// =============================================================================
// Tasks
// =============================================================================

/**
 * `charge` is now DISPATCHABLE: an operator sends the robot to a
 * charger, and the worker builds it as an RMF patrol of exactly one
 * place (mirroring `station_docking`'s builder). The operand places
 * are SITE-DECLARED — `NEXSOSS_CHARGER_PLACES`, intersected with the
 * live nav graph, the same route the dustbag stations take — so the
 * form offers it only where the site names a reachable charger, and
 * `TaskDispatchForm`'s own category union carries it.
 *
 * RMF still produces `charge` rows of its own — the low-battery
 * emergency retreat and the planner's inserted `ChargeBattery`
 * assignments — which project into this same category on the read
 * side; naming them here is what keeps them off `custom`.
 *
 * Note this is NOT the trip back to the charger that automatic
 * charging controls: that one is RMF's idle behaviour and is not a
 * task at all — it never reaches the task API.
 */
export type TaskCategory =
  | 'clean'
  | 'patrol'
  | 'delivery'
  | 'station_docking'
  /**
   * Empty the robot's dust bag. DISPATCHABLE, unlike `charge` above:
   * an operator asks for it, the robot drives to a DustBagStation and
   * waits there to be told the bag was emptied.
   *
   * It appears in this union AND in the dispatch form's own category
   * list, and it is offered only when the site's task catalog carries
   * it -- the stations are site-declared and intersected with the live
   * nav graph worker-side, so a site with no station never offers the
   * category at all.
   */
  | 'dustbag'
  | 'charge'
  | 'custom';

export type TaskPhaseKind = 'go_to_place' | 'perform_action' | 'wait' | 'charge';

export interface TaskPhase {
  readonly id: string;
  readonly kind: TaskPhaseKind;
  readonly description: string;
  /** Map of phase variables (place name, action type, etc.). */
  readonly params: Record<string, unknown>;
  readonly started_at_ms?: number;
  readonly completed_at_ms?: number;
  readonly skipped: boolean;
  /** Derived from RMF's completed/active/pending phase-id sets —
   *  individual phases carry no status of their own upstream. */
  readonly status?: 'pending' | 'active' | 'completed' | 'skipped';
}

export type TaskState = 'queued' | 'active' | 'completed' | 'failed' | 'cancelled' | 'interrupted';

export interface TaskRequest {
  readonly category: TaskCategory;
  readonly description: string;
  readonly phases: TaskPhase[];
  /** Optional fleet/robot scoping. */
  readonly fleet?: string;
  readonly robot?: string;
  /** Earliest start time (ms since epoch). Optional. */
  readonly earliest_start_ms?: number;
  /**
   * 0 Normal, 1 High (2026-10-31). Read every stored value through the
   * one bucketing rule: exactly the integer 1 is High, anything else --
   * null, a bool, the old 1-5 field's 2 to 5 -- is Normal. Only a
   * supervisor may set High; the api refuses anyone else (403
   * `capability_denied`, `detail.field: 'priority'`).
   */
  readonly priority: number;
  /**
   * JSON-Schema describing the dispatch form fields for this
   * category. The dispatch UI is schema-driven (plan §7.1).
   */
  readonly schema_ref: string;
}

/**
 * What a task was ASKED FOR, as opposed to what RMF confirmed happened.
 *
 * Read from the durable dispatch intent, because a QUEUED task's published
 * state carries no `phases` at all -- the dispatcher's queued state does
 * embed the request, but it is returned in the dispatch response and never
 * published, and the only queued sample the console sees is the fleet
 * adapter's pending-queue publish. So "where is this queued task going?"
 * is unanswerable from the live wire by construction.
 */
export interface TaskRequestRecord {
  readonly task_id: TaskId;
  readonly command_id: string;
  readonly submitted_at_ms: number;
  readonly requested_via?: 'dashboard' | 'calendar' | 'favorite' | null;
  readonly request: TaskRequest;
}

export interface TaskSummary {
  readonly id: TaskId;
  readonly state: TaskState;
  readonly category: TaskCategory;
  readonly description: string;
  readonly assigned_fleet?: string;
  readonly assigned_robot?: string;
  readonly submitted_at_ms: number;
  readonly started_at_ms?: number;
  readonly completed_at_ms?: number;
  readonly current_phase_id?: string;
  readonly phase_index: number;
  /** RMF `booking.requester`, `"calendar:{name}"` when dispatched via
   *  the calendar-slot flow, `"favorite:{name}"` when dispatched by
   *  running a saved favourite, or absent when the task wasn't
   *  created through this dashboard at all. */
  readonly triggered_by?: string;
  /** Ordered "waypoints visited" record — patrol places, delivery
   *  pickup(s)/dropoff(s), or the cleaning area, whichever the
   *  task's own go-to-place phases resolve to. */
  readonly phases?: TaskPhase[];
  /** RMF's own live remaining-time estimate — populated while a task
   *  is underway, absent once terminal (use `completed_at_ms` then)
   *  or if RMF hasn't published one yet. */
  readonly estimated_remaining_sec?: number;
  /** RMF's estimate of the task's whole duration
   *  (`original_estimate_millis`), the one estimate a QUEUED task
   *  carries. Absent from workers before it was added (2026-10-22). */
  readonly estimated_duration_sec?: number | null;
  /** Why a terminal task ended the way it did, when the projector or
   *  RMF said. Carried for `failed` records. */
  readonly failure_reason?: string;
  /**
   * The PLATFORM wrote this terminal record, not RMF — the worker's
   * stall reaper closing out a task whose owning fleet adapter was
   * replaced mid-flight and which therefore can never publish another
   * `task_state_update` (the `patrol.dispatch-48` class, 2026-08-28).
   *
   * It is a real `failed` record and keeps its history, but it is not
   * a robot failure and must never be read as one: the api-server
   * suppresses `task_failed` alerts and Issues for it, and the SPA
   * says "closed by the platform" rather than blaming the robot.
   */
  readonly synthetic?: boolean;
  /**
   * RMF's own raw status string (`error`, `blocked`, `underway`, ...).
   *
   * DISPLAY AND LOG ONLY. No control decision may branch on it: an
   * attention task is `state: 'active'` plus one of the three attention
   * statuses, and what decides anything is `attention` below. Absent
   * from a worker older than 0.4.29 and under its
   * `NEXSOSS_TASK_ERROR_IS_TERMINAL` rollback lever.
   */
  readonly rmf_status?: string;
  /**
   * "Still running, and a human should look at it."
   *
   * RMF's `error` is NOT a terminal -- the fleet adapter's `GoToPlace`
   * re-enters it every 5 s while it retries a plan. Worker 0.4.28
   * mapped it to `failed`, which is why Cancel and Kill vanished for 47
   * minutes on 2026-09-15 while a robot sat wedged at a disarmed lift,
   * an alert fired about a robot that was fine, and `task_completions`
   * recorded a failure at 12:37:38 for a task cancelled at 13:25:51.
   *
   * `since_ms` is when attention BEGAN, not when this sample arrived.
   */
  readonly attention?: {
    readonly rmf_status: 'error' | 'failed' | 'blocked';
    readonly since_ms: number;
    readonly reason?: string;
  };
  /** When RMF began saying it can find this task no way to its next leg, and
   *  null once it stops. Apart from `attention`: the task's status can be
   *  `standby`, which is no attention task. It names no lift and has other
   *  causes than a blocked one, so `lift-rule-waiting.ts` says what it means. */
  readonly no_route_since_ms?: number | null;
  /** Which structural signal ended the task, when the PLATFORM is the
   *  one saying it ended. A `failed` record with an attention
   *  `rmf_status` and no `settled_by` is the false terminal, and the
   *  api-server refuses to record one. */
  readonly settled_by?: 'rmf_released' | 'dispatcher_no_bid' | 'clean_failure_marker';
  /** Who CANCELLED it, when the canceller told RMF (worker 0.4.37): today
   *  only the platform's boarding retry, `system:boarding_retry`.
   *  `triggered_by` is who dispatched it. Null for an operator's cancel. */
  readonly cancelled_by?: string | null;
  /** 1 for a High task (worker 0.4.40: its dispatch label, else RMF's own
   *  booking priority); absent or null for Normal and from older workers. */
  readonly priority?: number | null;
}

export interface TaskDetail extends TaskSummary {
  readonly phases: TaskPhase[];
  readonly phases_total: number;
  readonly phases_completed: number;
  readonly submitter_sub: string;
  readonly command_id: string;
}

export interface TaskLogEntry {
  readonly task_id: TaskId;
  readonly at_ms: number;
  readonly level: 'debug' | 'info' | 'warn' | 'error';
  readonly text: string;
}

// =============================================================================
// Maps and building-map snapshot (plan §Map contract)
// =============================================================================

export interface BuildingMapAffine {
  /** Translation in site-frame coordinates. */
  readonly translation_x: number;
  readonly translation_y: number;
  /** Rotation in radians. */
  readonly rotation_rad: number;
  /** Uniform scale. */
  readonly scale: number;
}

export interface BuildingMapDimensions {
  readonly width_m: number;
  readonly height_m: number;
}

export interface BuildingMapSourceImage {
  readonly width_px: number;
  readonly height_px: number;
}

export interface BuildingMapImagePayload {
  /**
   * Image encoding. Only `image/webp` and `image/png` are valid in
   * production. Other encodings produce an operator-visible
   * degraded-map diagnostic (plan §Map contract: "Normalize to a
   * browser-safe WebP/PNG format with measured hard caps on
   * dimensions and encoded size. ... oversize or invalid assets
   * produce an operator-visible degraded-map diagnostic.").
   */
  readonly encoding: 'image/webp' | 'image/png';
  /** Base64-encoded normalized image bytes. */
  readonly data_base64: string;
  readonly content_hash_sha256: string;
  readonly normalized_dimensions_px: BuildingMapSourceImage;
}

export interface BuildingMapSnapshot {
  readonly site: SiteId;
  readonly level: string;
  readonly revision: string;
  readonly source_dimensions_m: BuildingMapDimensions;
  readonly affine: BuildingMapAffine;
  readonly image: BuildingMapImagePayload;
  readonly walls: ReadonlyArray<{
    readonly x1: number;
    readonly y1: number;
    readonly x2: number;
    readonly y2: number;
  }>;
  readonly nav_graph?: unknown;
  readonly published_at_ms: number;
  readonly schema_version: '1.0';
}

// =============================================================================
// Trajectories (separately budgeted live stream — not cached in RocksDB)
// =============================================================================

export interface TrajectoryPoint {
  readonly t_ms: number;
  readonly x: number;
  readonly y: number;
  readonly yaw: number;
}

export interface TrajectorySnapshot {
  readonly id: RobotId;
  readonly map: string;
  readonly segments: TrajectoryPoint[][];
  readonly updated_at_ms: number;
}

// =============================================================================
// Command lifecycle (plan §Command lifecycle)
// =============================================================================

export type CommandState =
  | 'received'
  | 'authorized'
  | 'published'
  | 'accepted'
  | 'observed_applied'
  | 'rejected'
  | 'timed_out'
  | 'outcome_unknown';

export interface CommandRecord {
  readonly command_id: string;
  readonly site: SiteId;
  readonly capability: CapabilityId;
  readonly target: Record<string, unknown>;
  readonly state: CommandState;
  readonly idempotency_key: string;
  readonly submitted_by_sub: string;
  readonly submitted_at_ms: number;
  readonly state_changed_at_ms: number;
  readonly outcome_message?: string;
  readonly correlation?: {
    readonly rmf_request_id?: string;
    readonly worker_published_at_ms?: number;
    /** RMF-assigned task id, populated on a successful `task.dispatch`. */
    readonly task_id?: string;
  };
}

// =============================================================================
// Lift session request/response (plan §Command lifecycle, server-owned)
// =============================================================================

export interface AcquireLiftSessionRequest {
  readonly lift: LiftId;
  readonly command_id: string;
}

export interface AcquireLiftSessionResponse {
  readonly session: LiftSession;
}

export interface ReleaseLiftSessionRequest {
  readonly session_id: string;
}

// =============================================================================
// Task dispatch request/response (plan §Command lifecycle)
// =============================================================================

export interface DispatchTaskRequest {
  readonly task: TaskRequest;
  readonly idempotency_key: string;
  /** Optional precondition — accept only if the worker's epoch
   * matches this value. The SPA passes the current epoch so a
   * stale UI cannot dispatch into a worker that has since restarted. */
  readonly expected_worker_epoch?: number;
  /** Which UI flow originated this dispatch — drives the "Triggered
   *  By" column's "Calendar (name)" vs plain "name" display only. */
  readonly requested_via?: 'dashboard' | 'calendar';
}

export interface DispatchTaskResponse {
  readonly command: CommandRecord;
  readonly task: TaskSummary;
  /** True when a favourite saved as High was run by someone who may not
   *  set High: it went as Normal, and the console should say so
   *  (api-server 0.6.56). Absent from an older api-server. */
  readonly priority_lowered?: boolean;
}

// =============================================================================
// Task favourites — saved dispatch templates for one-click re-run
// =============================================================================

/**
 * A saved, named `TaskRequest` template. Personal (`is_shared: false`)
 * favourites are visible only to `owner_sub`; shared ones are visible
 * to every caller assigned to the site — see `docs/adr` for the
 * reasoning (`is_shared` is also the intended gate for a future
 * non-browser/MQTT trigger, so a favourite that isn't shared can never
 * be run by anything other than its owner's own browser session).
 */
export interface TaskFavorite {
  readonly id: string;
  readonly site: SiteId;
  readonly name: string;
  readonly is_shared: boolean;
  readonly owner_sub: string;
  readonly category: TaskCategory;
  readonly description: string;
  readonly phases: TaskPhase[];
  readonly priority: number;
  readonly schema_ref: string;
  readonly created_at_ms: number;
}

export interface CreateTaskFavoriteRequest {
  readonly name: string;
  readonly is_shared: boolean;
  readonly category: TaskCategory;
  readonly description: string;
  readonly phases: TaskPhase[];
  readonly priority: number;
  readonly schema_ref: string;
}

export interface ExecuteTaskFavoriteRequest {
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

// --- Support tickets (hardening plan Phase 3, 3g) ----------------------------

export interface CreateSupportTicketRequest {
  readonly title: string;
  readonly body: string;
  readonly severity?: 'info' | 'warning' | 'critical';
  /** The report form's answers (`api/tickets.py`). */
  readonly category?: 'robot' | 'lift_door' | 'app' | 'other';
  readonly subject?: string;
  readonly quick_pick?: string;
  readonly issue_id?: string;
  readonly alert_id?: string;
}

export interface SupportTicket {
  readonly ticket_id: string;
  readonly site: SiteId;
  readonly requester_sub: string;
  readonly requester_display_name: string | null;
  readonly title: string;
  readonly body: string;
  readonly state: 'new' | 'triaging' | 'waiting_on_user' | 'resolved' | 'closed';
  readonly severity: 'info' | 'warning' | 'critical';
  readonly assignee_sub: string | null;
  readonly created_at_ms: number;
  readonly updated_at_ms: number;
  readonly resolved_at_ms: number | null;
  readonly external_ref: string | null;
  /** The last message this reader may see; on the list only, and absent
   *  from an older api-server. */
  readonly latest_message?: {
    readonly author: string | null;
    readonly body: string;
    readonly at_ms: number;
  } | null;
}

/**
 * One message on a ticket.
 *
 * `visibility` is decided by the SERVER. A message written by anyone
 * outside the Nexsoss triage roles is always `requester_visible`, and
 * `internal` messages are filtered out of what this app is given — so
 * an operator holding one of these is holding something meant for the
 * conversation, by construction.
 */
export interface SupportTicketComment {
  readonly id: number;
  readonly ticket_id: string;
  readonly author_sub: string;
  readonly author_display_name: string | null;
  readonly body: string;
  readonly visibility: 'internal' | 'requester_visible';
  readonly created_at_ms: number;
}

/** A ticket with its conversation and the snapshot taken when it was filed. */
export interface SupportTicketDetail extends SupportTicket {
  readonly context_snapshot: Record<string, unknown>;
  readonly correlation_candidates: ReadonlyArray<{
    readonly kind: string;
    readonly id: string | null;
    readonly title: string;
    readonly severity: string;
    readonly confidence: number | null;
  }>;
  readonly comments: SupportTicketComment[];
}

export interface TaskControlRequest {
  readonly action:
    | 'cancel'
    | 'interrupt'
    | 'kill'
    | 'resume'
    | 'rewind'
    | 'skip_phase'
    | 'undo_phase';
  readonly phase_id?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface TaskControlResponse {
  readonly command: CommandRecord;
}

// =============================================================================
// Door command (plan §Command lifecycle)
// =============================================================================

export interface DoorCommandRequest {
  readonly action: 'open' | 'close';
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface DoorCommandResponse {
  readonly command: CommandRecord;
}

// =============================================================================
// Door manual-open override (W8b, 2026-09-01)
//
// Hand-written mirror of `v1_contract.schemas`'s "Door manual-open
// override" section (`DoorOverrideAttestation`/`DoorOverrideRequest`/
// `DoorOverrideResponse`). `src/api/generated/` is regenerated from the
// api-server's OpenAPI by a separate step and must not be edited here.
//
// THE VERB RIDES THE WORKER, not the api-server: the api-server cannot
// reach a site's lifecycle broker (NetworkPolicy -- verified live,
// connection refused) and the worker can (verified live, HTTP 401 from
// the broker's own handler, i.e. packets arrive). Nothing in this file
// depends on that, but it is why the response is a `CommandRecord` and
// not the broker's own record: what comes back is an ADMISSION, and
// the console must not render it as a granted override.
// =============================================================================

/**
 * What the operator is promising. A FIXED CHECKLIST, not prose.
 *
 * THE POINT OF THE STRUCTURE. The broker's `attestation` field is a
 * free string and the obvious shape here was one too. A free string is
 * unauditable in both directions: nothing stops an empty-ish "ok"
 * being recorded as the reason a robot was driven at a closed door,
 * and nothing lets a reviewer later ask "did anyone ever claim they
 * could SEE it?".
 *
 * ALL THREE ARE REQUIRED AND ALL THREE MUST BE `true`. They have no
 * defaults in the contract and none here: the api-server answers a
 * `false` or an omitted box with a 422, and it must, because storing a
 * `false` would produce an audit entry that reads as an attestation
 * and is not one. So the dialog that collects these has to GATE its
 * submit on all three rather than send what it has -- see
 * `DoorOverrideDialog`.
 */
export interface DoorOverrideAttestation {
  readonly i_can_see_the_door: boolean;
  readonly the_door_is_fully_open: boolean;
  readonly i_will_keep_it_open_until_the_robot_has_passed: boolean;
  /** Anything the operator wants on the record beyond the checklist
   *  ("propped with the fire extinguisher", "engineer on site").
   *  Max 500 chars; the api-server flattens it to one printable line
   *  before it reaches the broker's log and event trail. */
  readonly note?: string | null;
}

/**
 * `POST /api/v1/sites/{site}/doors/{door}/override`.
 *
 * `extra="forbid"` server-side: an unknown key is a 422, not a
 * silently-ignored field. In particular there is no `action` -- the
 * verb is the HTTP method, because grant and revoke are separate
 * capabilities and folding them into one enum value would make a
 * single grant reach both.
 */
export interface DoorOverrideRequest {
  readonly attestation: DoorOverrideAttestation;
  /**
   * Seconds the override may live. Optional; the contract's default is
   * `DOOR_OVERRIDE_DEFAULT_TTL_S`.
   *
   * There is NO upper bound in the schema and that is deliberate: the
   * api-server CLAMPS to `DOOR_OVERRIDE_MAX_TTL_S` and reports the
   * granted value back at `response.command.target.ttl_s`, which
   * serves an operator standing in front of a door better than a 422
   * would. Zero and negatives ARE refused. So the console must read
   * the granted TTL back rather than assume it got what it asked for.
   */
  readonly ttl_s?: number;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/** Answered by BOTH the grant and the revoke. The granted TTL is at
 *  `command.target.ttl_s`. */
export interface DoorOverrideResponse {
  readonly command: CommandRecord;
}

/**
 * The TTL granted when a request names none. Three minutes: long
 * enough for a robot already standing at the door to cross, short
 * enough that a forgotten override lapses well inside the shift that
 * declared it. Mirrors `DOOR_OVERRIDE_DEFAULT_TTL_S` in the contract.
 */
export const DOOR_OVERRIDE_DEFAULT_TTL_S = 180;

/**
 * The hard ceiling, enforced by CLAMPING api-server-side rather than
 * by refusing the request. Mirrored here only so the form can say what
 * will happen BEFORE the press; the granted value still comes back on
 * the response and is what the console counts down from.
 */
export const DOOR_OVERRIDE_MAX_TTL_S = 1800;

// =============================================================================
// Dustbag acknowledgement (W11, 2026-09-01)
// =============================================================================

/**
 * `POST /api/v1/sites/{site}/robots/confirm_dustbag` -- "the bag is
 * empty, carry on".
 *
 * Addresses the ROBOT, not the task, and carries `task_id` only when
 * the console knows it. That asymmetry is what lets the latch survive
 * an emergency re-invoke: an interrupted `PerformAction` is
 * re-executed FROM SCRATCH on resume with no callback to the adapter,
 * so a confirm keyed to the robot alone still lands, and one that
 * names the task lets the worker match it to the execution it belongs
 * to. `extra="forbid"` server-side, and `robot.site` must equal the
 * path site or the api-server answers 400.
 */
export interface RobotConfirmDustbagRequest {
  readonly robot: RobotId;
  /** Omitted or null when the console does not know which task is
   *  waiting -- the robot drawer often does not. */
  readonly task_id?: string | null;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface RobotConfirmDustbagResponse {
  readonly command: CommandRecord;
}

/** "I have confirmed which floor this robot is on" — releases the hold
 *  the fleet adapter takes for a fire alarm. No `task_id`: the hold is
 *  on the ROBOT and outlives the task that was running. */
export interface RobotFireRelocalizeRequest {
  readonly robot: RobotId;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface RobotFireRelocalizeResponse {
  readonly command: CommandRecord;
}

// =============================================================================
// Lift command (server-owned session required)
// =============================================================================

export interface LiftCommandRequest {
  readonly session_id: string;
  /** `request_destination` is RETIRED (410 from api-server 0.6.8; the
   *  worker publishes nothing for it). Use `LiftManualCallRequest`.
   *  `set_mode` stays for one release as the legacy drain tool. */
  readonly action: 'request_destination' | 'set_mode';
  readonly destination_level?: string;
  /** Only meaningful for `action: 'request_destination'` — upstream's
   *  own lift request form treats this as a required third input
   *  alongside destination + request type; omitting it defaults to
   *  `'closed'` on the worker. */
  readonly door_mode?: 'open' | 'closed';
  readonly mode?: LiftMode;
  /** Force-release/redirect a lift held under a DIFFERENT RMF
   *  session (e.g. a stuck robot/fleet-adapter) — pass back whatever
   *  is currently shown as `LiftState.rmf_session_id`. Omit to use
   *  the worker's own stable per-lift session as usual. */
  readonly override_rmf_session_id?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface LiftCommandResponse {
  readonly command: CommandRecord;
}

// =============================================================================
// Lift abort — the RECOVERY verb, and deliberately not a LiftCommand
// =============================================================================

/**
 * Recover one car whose ride will not end on its own.
 *
 * NOT `set_mode: 'off'`. That becomes `session.close` on the bridge,
 * which releases AGV immediately — with a robot still in the car it goes
 * back into public service carrying a passenger nobody expects. Abort
 * runs the bridge's recall sequence instead: bring the car back, hold the
 * doors, wait for the robot to clear, alert an operator, then release.
 *
 * NO `session_id`, unlike `LiftCommandRequest`. A recovery is precisely
 * the case where the car is held by somebody ELSE — usually the robot
 * fleet adapter under its own id — so requiring the caller to hold the
 * nexsoss lift session would refuse every abort that matters.
 */
export interface LiftAbortRequest {
  /** Which recovery the bridge runs. `A`: the robot is reversing out at
   *  the entry floor. `B`: the car ran on and must come back for egress.
   *  `C`: the robot is stuck in the doorway at the destination and only a
   *  human can help. Defaults to `A` server-side. */
  readonly situation?: 'A' | 'B' | 'C';
  readonly reason?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/**
 * "Release lift" — supply the ONE confirmation the session is waiting on.
 *
 * NOT the old button of that name, which sent `set_mode: off` and tore
 * the whole session down. This advances the ride by exactly one wait;
 * pressing again walks it forward another step.
 *
 * `expected_phase` is echoed back from the lift's own reported state and
 * the bridge refuses a mismatch: a stalled session can move the instant
 * it un-stalls, and a confirmation given for one step must never land on
 * a different one.
 */
export interface LiftAdvanceRequest {
  readonly expected_phase: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/**
 * "Recover stuck robot" — end the lift session, release the car, and
 * cancel the robot's task.
 *
 * The task half is composed server-side. Without it RMF simply
 * re-requests the lift for a task that is still running.
 */
export interface LiftRecoverRequest {
  readonly reason?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/**
 * "End RMF session" — hand the car back AS IT IS.
 *
 * It does not move a robot first and does not wait for one. If a robot
 * is in the car or the doorway, the lift returns to public service with
 * it there — which is why this is a different verb from Recover, with a
 * different capability and its own confirmation.
 *
 * `override_rmf_session_id` is REQUIRED: omitting it targets a session
 * the supervisor will never match, so the release is silently discarded.
 */
export interface LiftEndSessionRequest {
  readonly override_rmf_session_id: string;
  readonly reason?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface LiftAbortResponse {
  readonly command: CommandRecord;
}

/**
 * What every OPERATOR verb route answers with (advance, recover,
 * end_session, call, close). `/abort` keeps `LiftAbortResponse`.
 *
 * `request_id` is minted server-side (`<verb>-<hex>`) and is the ONLY
 * thing the console correlates the outcome on: the adapter answers by
 * writing `LiftState.session.last_result` keyed by it. `deadline_s` is
 * the api-server's promise of how long that will take at most; the
 * drawer's in-flight state gives up at `deadline_s + 5` with NO_VERDICT.
 */
export interface LiftVerbResponse {
  readonly command: CommandRecord;
  readonly request_id: string;
  readonly verb: string;
  readonly lift: string;
  readonly deadline_s: number;
}

/**
 * "Request Floor" -- an operator-owned one-shot call of the car.
 *
 * `floor` is the RMF floor LABEL (e.g. L13). There is deliberately no
 * door mode: the edge's session family has no door-mode input, and
 * carrying a field the edge drops is a UX misread waiting to happen
 * ("Open" chosen, doors closed). `session_id` is the PANEL lease
 * (`/lift/sessions`), still required so two operators cannot drive one
 * car at once -- it is not, and never becomes, the RMF session.
 */
export interface LiftManualCallRequest {
  readonly session_id: string;
  readonly floor: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/** "Cancel call" -- end the operator-owned session. Admitted from any
 *  phase, including while the call itself is still in flight. */
export interface LiftManualCloseRequest {
  readonly idempotency_key: string;
  readonly reason?: string;
  readonly expected_worker_epoch?: number;
}

// =============================================================================
// Robot commission/decommission (plan §7.2)
// =============================================================================

/**
 * Which of upstream's three commission flags to re-enable. Only
 * meaningful for `action: 'commission'` — `decommission` sets all three
 * false unconditionally. Omitted, the worker applies upstream's own
 * default (all true).
 */
export interface RobotCommission {
  readonly dispatch_tasks: boolean;
  readonly direct_tasks: boolean;
  readonly idle_behavior: boolean;
}

export type PendingDispatchTasksPolicy = 'reassign' | 'complete' | 'cancel';
export type PendingDirectTasksPolicy = 'complete' | 'cancel';

export interface RobotCommissionRequest {
  readonly robot: RobotId;
  readonly action: 'commission' | 'decommission';
  readonly commission?: RobotCommission;
  /**
   * What RMF does with work already QUEUED to this robot. Unset,
   * upstream defaults to `reassign` (move it to another robot in the
   * same fleet). None of these policies touch the task the robot is
   * currently EXECUTING — that must be interrupted or cancelled
   * separately.
   */
  readonly pending_dispatch_tasks_policy?: PendingDispatchTasksPolicy;
  readonly pending_direct_tasks_policy?: PendingDirectTasksPolicy;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

export interface RobotCommissionResponse {
  readonly command: CommandRecord;
}

// -----------------------------------------------------------------------------
// Automatic charging (RMF idle behaviour) — `robot.set_idle_behavior`
//
// Upstream holds the flag in RAM and defaults it to true on every
// `add_robot`, so a fleet-adapter restart silently switches automatic
// charging back on and the robot drives off at once. The api-server
// therefore stores the operator's intent as a row and re-asserts it;
// what the SPA reads back here is that DESIRED state, which is why the
// drawer shows it against the live flag rather than instead of it.
// -----------------------------------------------------------------------------

export interface RobotIdleBehaviorRequest {
  readonly robot: RobotId;
  readonly enabled: boolean;
  /**
   * Minutes until automatic charging switches itself back on.
   * REQUIRED when turning it off, because turning it off also
   * disables the low-battery retreat to the charger — a robot left
   * this way indefinitely will flatten itself where it stands. The
   * api-server allows an open-ended `null` for `platform_admin` only.
   * Ignored when `enabled` is true.
   */
  readonly ttl_minutes?: number | null;
  readonly reason?: string;
  readonly idempotency_key: string;
  readonly expected_worker_epoch?: number;
}

/** One robot whose automatic charging is currently switched OFF.
 *  There is no row for a robot in the normal state — RMF's own default
 *  is on, so turning it back on DELETES the row rather than storing a
 *  `true`. */
export interface RobotIdlePolicy {
  readonly robot: RobotId;
  readonly idle_enabled: boolean;
  /** Epoch ms, or null for "until turned back on". */
  readonly expires_at_ms: number | null;
  readonly reason: string | null;
  readonly set_by_sub: string;
  readonly set_at_ms: number;
}

export interface RobotIdleBehaviorResponse {
  readonly command: CommandRecord;
  /** Null after a successful `enabled: true` — the row is gone. */
  readonly policy: RobotIdlePolicy | null;
}

// =============================================================================
// Recurring task schedules
//
// A schedule stores an RFC 5545 recurrence rule and the task payload to
// replay, never a materialised list of future occurrences: the calendar
// expands the rule on read and the runner expands it on each tick, so a
// rule edit takes effect everywhere at once with no backfill.
//
// Occurrence identity is `original_start_ms` — the instant the RULE
// produces, before any per-occurrence override moves it. Exdates and
// overrides key on that, so "skip the 2 PM clean" keeps meaning the same
// occurrence after it has been dragged to 3 PM.
// =============================================================================

export type ScheduleFireStatus =
  | 'dispatched'
  | 'failed'
  | 'missed'
  | 'skipped_fire_mode'
  /** Came due inside a planned maintenance window. Dropped, not
   *  deferred — a 02:00 clean released at 05:00 arrives with guests
   *  about. The row is what tells the operator it did not run. */
  | 'skipped_maintenance';

export interface TaskScheduleFields {
  readonly name: string;
  readonly task: TaskRequest;
  /** RRULE body without the `RRULE:` prefix, e.g. `FREQ=WEEKLY;BYDAY=MO,WE,FR`. */
  readonly rrule: string;
  readonly dtstart_ms: number;
  /**
   * IANA zone the rule expands in. Recurrence is wall-clock: a 09:00
   * daily clean stays at 09:00 across a DST shift.
   */
  readonly timezone: string;
  readonly until_ms?: number | null;
  readonly enabled: boolean;
  readonly color?: string | null;
}

export interface TaskSchedule extends TaskScheduleFields {
  /** `sched_<32 hex>` as created. The second schedule a retime splits
   *  off to keep the old time is `<root>.<n>`, the root being the id it
   *  came from up to its first "." -- every schedule of one plan,
   *  however many edits split it, shares that root, and nothing else
   *  does (api-server 0.6.54). */
  readonly schedule_id: string;
  readonly site: SiteId;
  readonly category: TaskCategory;
  readonly exdates_ms: number[];
  readonly created_by_sub: string;
  /**
   * Who `created_by_sub` is, resolved at READ time from the directory of
   * everyone who has signed in (the way `AuditEntry.actor_*` are) — no
   * column stores them, so a person who changes their name shows the new
   * one on every schedule they ever made. Null when the subject has no
   * directory row; never the subject itself. Optional because an
   * api-server older than 0.6.54, and every older fixture, sends
   * neither. Render through `personLabel` (`calendar/people.ts`), which
   * never falls back to the raw id.
   */
  readonly created_by_email?: string | null;
  readonly created_by_display_name?: string | null;
  readonly created_at_ms: number;
  readonly updated_at_ms: number;
  /**
   * The newest run it has an outcome for (the rule instant, from the
   * fire ledger at read time); null when it never ran. A retime moves
   * `dtstart_ms` onto the first NEW run, often days ahead, so a start
   * still to come does not mean it has no past -- this says it has one
   * (`hasScheduleHistory`). Optional: an api-server older than 0.6.54
   * sends none.
   */
  readonly last_run_ms?: number | null;
}

export type CreateTaskScheduleRequest = TaskScheduleFields;

export type UpdateTaskScheduleRequest = Partial<TaskScheduleFields> & {
  /**
   * Where a TIMING change (a different `rrule`, `dtstart_ms` or
   * `timezone`) takes effect, epoch ms. Ignored by every other change.
   *
   * A schedule that has already run is RE-ANCHORED in place rather than
   * rewritten: runs before the cut keep the old rule and their recorded
   * outcomes, and the new rule — seeded by the request's `dtstart_ms`,
   * which fixes its time of day, weekday and day of month — starts at
   * its first instant on or after the cut. The server takes the cut as
   * the LATEST of this, now, and just after the schedule's last recorded
   * run, so nothing sent here can rewrite the past; omitted means "from
   * now". Same `schedule_id` before and after (api-server 0.6.54).
   *
   * An older api-server forbids unknown fields and answers 422.
   */
  readonly apply_from_ms?: number | null;
};

/**
 * `POST /schedules/{id}/preview` — what the same body would do as a
 * PATCH, computed by the same server code and written nowhere (no row,
 * no audit). It exists for a live line under an editor, so an operator
 * sees the next runs, and what an edit throws away, BEFORE saving.
 */
export interface ScheduleUpdatePreview {
  /** Whether the server reads the body as a TIMING change (re-anchor)
   *  rather than an in-place edit. */
  readonly timing_change: boolean;
  /** The saved schedule's first run at or after now; null when none is
   *  left (ended, or its count used up). */
  readonly first_run_ms: number | null;
  /** Up to five runs of the saved schedule at or after now, after the
   *  skips and moves that survive the edit — where runs LAND. */
  readonly next_runs_ms: number[];
  /** Skips (exdates) and moves (overrides) on or after the cut that the
   *  new rule no longer produces, which saving deletes. */
  readonly dropped_skips: number;
  readonly dropped_moves: number;
  /** The same skips and moves by the instant each was keyed on (the old
   *  rule's), ascending, so the editor names the DAY a skip stops
   *  covering. Optional: an api-server before them sends counts only. */
  readonly dropped_skips_ms?: number[];
  readonly dropped_moves_ms?: number[];
  /** An old-rule run later TODAY, not yet fired, that the new rule will
   *  not produce at its own instant — so the editor can say "today's
   *  09:00 run won't happen" (or, with `first_run_ms` the same day, that
   *  it moves). "Today" is the SCHEDULE's zone's day (`timezone`), which
   *  is what api-server 0.6.54 implements; the `sites` column it would
   *  otherwise read is not populated. Null for a paused schedule, and
   *  when there is none. A run listed in `kept_runs_ms` is not lost. */
  readonly lost_today_ms: number | null;
  /** Old-rule runs still to come before the change day -- the day
   *  `first_run_ms` is on, or a later "Applies from", or else tomorrow --
   *  which keep their old TIME: saving puts them on a SECOND schedule
   *  (its own `schedule_id`, the old rule, the edited name, task and
   *  colour, ending the moment before that day) — the "Applies from" gap
   *  and a new time already past today no longer drop them. An earlier
   *  schedule of the same plan, cut short at that day, lists its runs
   *  before it here too. The first five, where they land, ascending;
   *  empty when the edit leaves none behind. `first_run_ms`/`next_runs_ms`
   *  stay the edited schedule's own (api-server 0.6.54). */
  readonly kept_runs_ms: number[];
  /** When that second schedule ends (its `until_ms`); null when the edit
   *  makes none. */
  readonly kept_until_ms: number | null;
  /** The other schedules of the same plan saving cuts short at the
   *  change day, or removes, earliest start first -- the old time an
   *  earlier edit kept, listed separately, that the new time replaces
   *  from that day. Their runs before it are in `kept_runs_ms` too.
   *  Empty when it clips none. Optional: an api-server before 0.6.54
   *  sends none. */
  readonly clipped?: readonly ClippedSchedule[];
  /** The schedule as it would be saved, creator names included. */
  readonly schedule: TaskSchedule;
}

/** One schedule a retime cuts short or removes
 *  (`ScheduleUpdatePreview.clipped`). */
export interface ClippedSchedule {
  readonly schedule_id: string;
  /** Its name as listed now, before the save. */
  readonly name: string;
  /** Saving deletes it: it is left with no run and has never run. */
  readonly removed: boolean;
  /** Its end once saved, the moment before the change day; null when
   *  it is removed. */
  readonly until_ms: number | null;
  /** How many of its runs not yet run it will no longer make: those
   *  from the change day to its old end, and any that came due minutes
   *  ago and have not started (recorded as missed). */
  readonly dropped_runs: number;
}

/** One expansion of a schedule's rule inside a queried window. Virtual:
 *  no row exists until it fires. */
export interface ScheduleOccurrence {
  readonly schedule_id: string;
  readonly site: SiteId;
  readonly name: string;
  readonly category: TaskCategory;
  readonly color?: string | null;
  /** Where it actually lands, after any override. */
  readonly start_ms: number;
  /** Stable identity: what the rule produced. */
  readonly original_start_ms: number;
  readonly overridden: boolean;
  /** Set only once it has come due and an outcome was recorded. */
  readonly status?: ScheduleFireStatus | null;
  readonly task_id?: string | null;
  /**
   * Why `status` is what it is, in the runner's words: the refusal the
   * command path gave, the lateness that put it past the catch-up
   * window, the fire-mode hold. Null while an occurrence is still in
   * the future.
   */
  readonly detail?: string | null;
  /**
   * The zone the schedule behind this occurrence expands in. Carried
   * per occurrence so the calendar can label the clock it draws
   * without joining back to the schedule list — a separate request
   * that can fail on its own.
   */
  readonly timezone?: string;
  /** A clean's mode (`vacuum` / `sweep` / `dust_mop` / `scrub`; an older
   *  api-server's `wet_scrub` is read as `scrub`) -- the override's
   *  task's, else the schedule's -- so the calendar draws it in its own
   *  type's lane. Absent from an older api-server: Vacuum's lane. */
  readonly clean_mode?: string | null;
  /** The fleet / robot the occurrence's task pins (the override's task,
   *  else the schedule's); null when RMF picks. Absent from an api-server
   *  older than the calendar lanes (2026-10-30) -- the lanes then join to
   *  the schedule's own `task` for the same answer. */
  readonly fleet?: string | null;
  readonly robot?: string | null;
  /** The level this run will be dispatched at, 0 Normal or 1 High: the
   *  override's task's, else the schedule's. Absent from an api-server
   *  older than 0.6.56 -- read the schedule's own `task.priority` then. */
  readonly priority?: number | null;
}

/**
 * `GET /schedules/occurrences` — the expansion plus whether it is
 * complete. An envelope rather than a bare list because the expansion
 * is capped server-side, and a short list is indistinguishable from a
 * quiet month on a calendar.
 */
export interface ScheduleOccurrencePage {
  readonly occurrences: ScheduleOccurrence[];
  /** True when expansion stopped at the cap. What is here is real;
   *  something is missing. */
  readonly truncated: boolean;
  /** The cap that was applied. */
  readonly limit: number;
}

export interface ScheduleOccurrenceOverride {
  readonly replacement_start_ms?: number | null;
  readonly task?: TaskRequest | null;
}

// =============================================================================
// Planned maintenance windows
//
// A period during which the site dispatches nothing: lift servicing,
// floor works, a function in the lobby. Site-wide — taking one robot out
// of service is `robot.decommission`.
//
// NOT fire mode. That is an emergency freeze: it interrupts running
// work, decommissions idle robots and has no planned window. A
// maintenance window refuses NEW dispatch and nothing else, so work
// already underway finishes and the robots then dock and charge on
// their own idle behaviour.
//
// One-off in v1. The gate evaluates a list of intervals rather than a
// single open window, so recurrence can be added without reshaping it.
// =============================================================================

export interface MaintenanceWindowFields {
  /** Half-open `[starts_at_ms, ends_at_ms)`. A task starting exactly on
   *  the end is allowed: "until 05:00" means work may start at 05:00,
   *  and two adjacent windows must not overlap by a millisecond. */
  readonly starts_at_ms: number;
  readonly ends_at_ms: number;
  /** What the operator typed. The only explanation anyone gets for why
   *  dispatch is being refused, so it reaches the calendar band and the
   *  refusal message. */
  readonly reason?: string | null;
}

export interface MaintenanceWindow extends MaintenanceWindowFields {
  readonly window_id: string;
  readonly site: SiteId;
  readonly created_by_sub: string;
  /** Who booked it, resolved at read time exactly as
   *  `TaskSchedule.created_by_*` are, and optional for the same reason. */
  readonly created_by_email?: string | null;
  readonly created_by_display_name?: string | null;
  readonly created_at_ms: number;
}

export type CreateMaintenanceWindowRequest = MaintenanceWindowFields;

export type UpdateMaintenanceWindowRequest = Partial<MaintenanceWindowFields>;

// =============================================================================
// Month plan check (api-server 0.6.55, Akash 2026-10-31)
//
// `GET /sites/{site}/calendar/plan-check?from_date&to_date`: for each
// cleaning zone (the zone list Metrics uses), the days from today on where
// neither a clean on record nor a planned run keeps it inside its Front of
// house / Back of house target ("clean every N days"). Dates are the
// SITE's calendar (`YYYY-MM-DD`). Only the roles that edit schedules and
// the Metrics roles may read it; the Month view does not ask for anyone
// else.
//
// A planned run is one the runner will still clean with: an enabled
// schedule's run still to come and not in blocked time; one that started
// 5 minutes ago or less and has not gone out yet (the runner's catch-up
// window); or one under way. One under way, and one from before midnight
// still inside its 5 minutes, count on today (decision 10, Akash
// 2026-10-31).
// =============================================================================

export type PlanCheckZoneGroup = 'foh' | 'boh';

export interface PlanCheckDay {
  readonly date: string;
  /** Today or later. A past day shows what happened and is not checked. */
  readonly checked: boolean;
  /** Zones past their target that day, not counting the zones with no
   *  counted run from `checked_from` to `horizon_to_date`
   *  (`unscheduled_zones`); 0 when not checked. */
  readonly short_zones: number;
}

/** One run of consecutive checked days a zone is past its target. */
export interface PlanCheckGap {
  readonly zone: string;
  readonly level: string | null;
  readonly group: PlanCheckZoneGroup;
  readonly target_days: number;
  readonly from_date: string;
  readonly to_date: string;
  /** Its last clean as Metrics counts it; null when none is on record. */
  readonly last_cleaned_ms: number | null;
  /** Its last planned run before `from_date`; null when none is planned
   *  before the gap. */
  readonly last_planned_ms: number | null;
}

/** A zone with no counted run from `checked_from` to `horizon_to_date`:
 *  listed once for the window, never counted as short day by day. */
export interface PlanCheckUnscheduledZone {
  readonly zone: string;
  readonly level: string | null;
  readonly group: PlanCheckZoneGroup;
  readonly target_days: number;
  readonly last_cleaned_ms: number | null;
  /** The first day from `checked_from` to `horizon_to_date` it is past
   *  its target, after `to_date` when that is when (decision 12, Akash
   *  2026-10-31); null only when it stays on target through
   *  `horizon_to_date`. Says when the zone goes short and nothing else: no
   *  day in `days` counts it. */
  readonly short_from_date: string | null;
}

export interface PlanCheck {
  readonly site: SiteId;
  /** IANA zone the dates are on. */
  readonly timezone: string;
  /** The site's today. */
  readonly today: string;
  readonly from_date: string;
  readonly to_date: string;
  /** The first checked day, `max(from_date, today)`; null when the whole
   *  window is in the past. */
  readonly checked_from: string | null;
  /** `max(to_date, checked_from + 27 days)`: a zone with no counted run
   *  from `checked_from` to this day is in `unscheduled_zones`; one with a
   *  run by then is short day by day like any other (decision 9, Akash
   *  2026-10-31). It is also as far as an unscheduled zone's
   *  `short_from_date` looks, so that one's null means on target through
   *  this day (decision 12). Later than `to_date` only when fewer than 28
   *  days are checked -- for a month, the current one near its end -- and
   *  `days` and `gaps` never go past `to_date`. Null exactly when
   *  `checked_from` is null. */
  readonly horizon_to_date: string | null;
  /** Target days for Front of house and Back of house. */
  readonly targets: { readonly foh: number; readonly boh: number };
  /** Where the zone list came from: the live map, or (when that could not
   *  be read) the zones with cleans on record. */
  readonly zone_source: 'registry' | 'cleans_on_record';
  /** Every day of the window, in order. */
  readonly days: PlanCheckDay[];
  /** By `from_date`, then zone. */
  readonly gaps: PlanCheckGap[];
  /** By floor (highest first), then zone. */
  readonly unscheduled_zones: PlanCheckUnscheduledZone[];
  readonly generated_at_ms: number;
}

// =============================================================================
// Fire mode
//
// Server-owned, per-site and durable: an alarm outlives the browser that
// raised it. While an event is open the command path refuses the
// dispatch-shaped capabilities with `fire_mode_active`, so the freeze
// does not depend on any client staying connected.
// =============================================================================

export type FireTriggerSource = 'lift' | 'manual';
export type FireResolution = 'resume' | 'cancel_all';

export interface FireEvent {
  readonly fire_event_id: string;
  readonly site: SiteId;
  readonly state: 'active' | 'resolved';
  readonly trigger_source: FireTriggerSource;
  /** Which lift recalled, for `trigger_source: 'lift'`. */
  readonly trigger_detail?: string | null;
  readonly triggered_by_sub?: string | null;
  readonly triggered_at_ms: number;
  readonly drill: boolean;
  /** Every task fire mode ATTEMPTED to interrupt, recorded before the
   *  command went out — a command can reach RMF and still fail on the
   *  way back, and an interrupted task never reappears in the
   *  enforcement sweep's active set. Recovery intersects this with live
   *  task state rather than trusting it outright. */
  readonly interrupted_task_ids: string[];
  readonly decommissioned_robots: string[];
  readonly resolved_at_ms?: number | null;
  readonly resolved_by_sub?: string | null;
  readonly resolution?: FireResolution | null;
  readonly notes?: string | null;
}

export interface FireModeState {
  readonly site: SiteId;
  readonly active: boolean;
  readonly event?: FireEvent | null;
  /** What resuming or cancelling would actually act on, computed the
   *  same way the server computes its target list: this event's
   *  `interrupted_task_ids` INTERSECTED with the tasks live state still
   *  reports as `interrupted`. Neither side alone is the answer — the
   *  record still names tasks that have since gone terminal, and the
   *  live set includes tasks an operator interrupted by hand before the
   *  alarm, which fire recovery does not own. */
  readonly interrupted_task_count: number;
  /** Not part of the resume set: the extra live work `cancel_all`
   *  additionally cancels, read at request time because tasks keep
   *  queueing up behind the freeze. */
  readonly queued_task_count: number;
}

export interface FireTriggerRequest {
  readonly drill: boolean;
  readonly notes?: string;
}

export interface FireResolveRequest {
  readonly resolution: FireResolution;
  readonly notes?: string;
}

export interface FireResolveResponse {
  readonly event: FireEvent;
  readonly succeeded_task_ids: string[];
  readonly failed_task_ids: string[];
  readonly recommissioned_robots: string[];
}

// =============================================================================
// Per-user notification preferences
// =============================================================================

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface UserNotificationPrefs {
  readonly email_enabled: boolean;
  readonly min_severity: AlertSeverity;
  /**
   * Which sites to be mailed about. `null` means "every site I am
   * assigned to" — the default, and the only state most operators ever
   * have. A non-empty list REPLACES that scope rather than adding to
   * it, so a list naming other sites silences this one.
   *
   * It exists for a `platform_admin`, who holds no assignment rows at
   * all and was therefore subscribed to silence. Never `[]` on the way
   * out: an emptied selection reads back as `null`, the same answer as
   * one that was never made. See `docs/notification-scope.md`.
   */
  readonly sites: readonly string[] | null;
}

/**
 * PATCH semantics: an omitted field keeps its stored value, so the
 * severity control and the site control cannot overwrite each other.
 *
 * `sites: []` is MEANINGFUL and distinct from omitting it — it clears
 * the selection, returning to "every site I am assigned to".
 */
export type UpdateUserNotificationPrefs = Partial<{
  email_enabled: boolean;
  min_severity: AlertSeverity;
  sites: readonly string[];
}>;

// =============================================================================
// Audit (paginated)
// =============================================================================

export interface AuditEntry {
  readonly audit_id: string;
  readonly site: SiteId;
  readonly actor_sub: string;
  /**
   * Who `actor_sub` is, resolved server-side from the directory of
   * everyone who has signed in. Both are absent when the subject is
   * unknown there — normal for the platform's own synthetic actors
   * (`system:fire_mode`, `system:idle_behavior`). Render `actor_sub`
   * then; see `formatActor`.
   */
  readonly actor_email?: string | null;
  readonly actor_display_name?: string | null;
  readonly actor_roles: string[];
  readonly action: string;
  readonly target: Record<string, unknown>;
  readonly command_id?: string;
  readonly at_ms: number;
  readonly outcome:
    | 'allowed'
    | 'denied'
    | 'published'
    | 'accepted'
    | 'rejected'
    | 'timed_out'
    | 'outcome_unknown';
  readonly reason?: string;
}

export interface PaginatedAudit {
  readonly entries: AuditEntry[];
  readonly next_cursor?: string;
  readonly total?: number;
}

// =============================================================================
// History (paginated, selected task/fleet history)
// =============================================================================

export interface PaginatedTaskLog {
  readonly entries: TaskLogEntry[];
  readonly next_cursor?: string;
}

/**
 * One finished task as the durable `task_completions` row has it --
 * `GET /sites/{site}/history/task_completions`, the Tasks tab's older
 * view. A summary written once when the task ended: no phases, no
 * failure reason, nothing controllable.
 *
 * `state` and `category` are plain strings on the wire (an archive must
 * not fail a page over one odd row). The writer only stores
 * `completed` / `failed` / `cancelled`; RMF's `killed` arrives as
 * `cancelled` with `rmf_status: 'killed'`.
 *
 * `submitted_at_ms` is the finish time on a few old rows whose terminal
 * record carried none. `completed_at_ms` on a `synthetic` row is when
 * the platform closed it out, not when a robot stopped.
 */
export interface TaskCompletionRecord {
  readonly task_id: TaskId;
  readonly category: string;
  readonly state: string;
  readonly assigned_fleet?: string | null;
  readonly assigned_robot?: string | null;
  readonly triggered_by?: string | null;
  readonly submitted_at_ms: number;
  readonly started_at_ms?: number | null;
  readonly completed_at_ms: number;
  /** Distinct places the task's phases named, in phase order. */
  readonly visited_places: ReadonlyArray<string>;
  /** The zone a clean was sent to; a clean names no place in its phases. */
  readonly clean_zone?: string | null;
  /** A clean's mode, read from its dispatch intent when the page was
   *  served. Null for other categories and for a clean with no intent
   *  on file -- shown as the vacuum every pre-mode clean was. */
  readonly clean_mode?: string | null;
  readonly synthetic: boolean;
  readonly rmf_status?: string | null;
  readonly settled_by?: string | null;
  readonly corrected_from?: string | null;
  /** api-server 0.6.48: `system:boarding_retry` when the platform cancelled it. */
  readonly cancelled_by?: string | null;
  // ---- distance travelled and cleaning time (api-server 0.6.41) --------
  //
  // OPTIONAL, and ALL null means "not measured" -- never 0 m. An older
  // api-server sends none of them; a task that finished before the fleet
  // adapter learned to count (schema 10), or ran on a fleet that does
  // not report an odometer, has them all null. A reader shows a dash.
  /** Wheel distance driven while this task was current, metres. */
  readonly distance_m?: number | null;
  /** The part of `distance_m` driven while the clean's action was open. */
  readonly clean_distance_m?: number | null;
  /** Seconds with the vendor's "clean running" flag on. INCLUDES the
   *  undock and the drive to where the coverage starts. */
  readonly active_clean_s?: number | null;
  /** The same, further requiring the cleaning head DOWN: time actually
   *  spent cleaning. What the Tasks tab's Cleaning time shows. */
  readonly brush_down_clean_s?: number | null;
  /** Stretches the odometer could not measure; > 0 makes every number
   *  above a LOWER BOUND. */
  readonly distance_discontinuities?: number | null;
  /** `distance_discontinuities > 0`, spelled out by the server. */
  readonly distance_partial?: boolean | null;
  /** False when the row holds the last RUNNING total rather than the
   *  adapter's final one (at most ~15 s of driving short). */
  readonly distance_final?: boolean | null;
  readonly distance_source?: string | null;
  // ---- the robot's own clean report (api-server 0.6.50) ----
  // Summed over the task's cleans. Null when the robot reported nothing --
  // never 0 m². A reader shows nothing, not a zero.
  /** Floor area the ROBOT says it covered, square metres. */
  readonly cleaned_area_m2?: number | null;
  /** How long the robot says it cleaned, seconds (its own clock). */
  readonly robot_clean_duration_s?: number | null;
  /** The reports themselves, one per clean, oldest first. */
  readonly clean_report?: ReadonlyArray<Readonly<Record<string, unknown>>> | null;
}

/** Newest finish first. Pass `next_cursor` back as `cursor`; absent on
 *  the last page. */
export interface PaginatedTaskCompletions {
  readonly entries: TaskCompletionRecord[];
  readonly next_cursor?: string | null;
}

// =============================================================================
// Distance travelled and cleaning time (worker 0.4.33, api-server 0.6.41)
// =============================================================================

/**
 * One task's running or final totals, live on
 * `rmf/v1/sites/{site}/task_distance/{task_id}` (about every 15 s while
 * the task runs, then three finals 5 s apart).
 *
 * A key of its OWN, deliberately not a field on `TaskSummary`: every v1
 * model is `extra="forbid"`, so a grown task payload would be refused
 * whole by a reader one release behind, and a key under `tasks/` would
 * be hydrated as a TaskSummary by an older console's `tasks/**` read.
 * Not stored on the bus: a console that opens mid-task catches up from
 * `GET .../history/task_distances` (`TaskDistanceList`).
 *
 * Every message carries the CUMULATIVE totals; the highest `at_ms` is the
 * right one, and at an equal `at_ms` the `final` flags are OR-ed. A
 * later running total after a final REOPENS the task (RMF resumed it).
 */
export interface TaskDistance {
  readonly site: string;
  readonly task_id: string;
  readonly fleet: string;
  readonly robot: string;
  readonly distance_m: number;
  readonly clean_distance_m: number;
  readonly active_clean_s: number;
  /** Null from an adapter that does not report the cleaning head. */
  readonly brush_down_clean_s?: number | null;
  readonly discontinuities: number;
  readonly source: string;
  readonly final: boolean;
  /** The adapter's clock at the last reading in these totals, epoch ms.
   *  The ordering key, not a receipt time. */
  readonly at_ms: number;
}

/** A task's latest totals from Postgres -- the catch-up read. */
export interface TaskDistanceRecord {
  readonly task_id: TaskId;
  readonly fleet?: string | null;
  readonly robot?: string | null;
  readonly distance_m: number;
  readonly clean_distance_m: number;
  readonly active_clean_s: number;
  readonly brush_down_clean_s?: number | null;
  readonly discontinuities: number;
  /** `discontinuities > 0`: every number is a lower bound. */
  readonly partial: boolean;
  readonly final: boolean;
  readonly source: string;
  readonly at_ms: number;
}

/** `GET .../history/task_distances?task_id=a&task_id=b` (at most 200
 *  ids). An id with no row -- never measured, or purged -- is absent. */
export interface TaskDistanceList {
  readonly entries: TaskDistanceRecord[];
}

// =============================================================================
// Manifests and tombstones (plan §Live consistency protocol)
// =============================================================================

export interface CategoryManifest {
  readonly site: SiteId;
  readonly category: 'fleets' | 'doors' | 'lifts' | 'tasks' | 'robots';
  /** Schema version of the manifest itself. */
  readonly schema_version: '1.0';
  /** Active entity IDs for this category. */
  readonly entities: ReadonlyArray<string>;
  /** Epoch + revision this manifest represents. */
  readonly epoch: number;
  readonly revision: number;
  readonly published_at_ms: number;
}

export interface Tombstone {
  readonly site: SiteId;
  readonly category: CategoryManifest['category'];
  readonly entity_id: string;
  readonly reason: 'removed' | 'decommissioned' | 'site_retired' | 'worker_restart';
  readonly published_at_ms: number;
  readonly epoch: number;
  readonly revision: number;
}

// =============================================================================
// Live v1 event envelope (every WebSocket / SSE message)
// =============================================================================

export type V1LiveEvent =
  | {
      readonly kind: 'snapshot';
      readonly category: 'fleets' | 'doors' | 'lifts' | 'robots' | 'tasks';
      readonly envelope: V1Envelope;
      readonly payload: FleetRegistry | DoorState | LiftState | RobotState | TaskSummary | unknown;
    }
  | {
      readonly kind: 'event';
      readonly category: 'fleets' | 'doors' | 'lifts' | 'robots' | 'tasks' | 'trajectories';
      readonly envelope: V1Envelope;
      readonly payload: unknown;
    }
  | {
      /**
       * Fire mode for the site. Its own kind rather than a live
       * category: it is one state per SITE, not one per entity, so it
       * has no id, no manifest and no reducer — and handlers must be
       * able to tell it apart from every other event, because a
       * subscriber that mistakes a robot update for this one silences
       * a live alarm.
       */
      readonly kind: 'fire_alarm';
      readonly envelope: V1Envelope;
      readonly payload: { trigger?: boolean; unix_millis_time?: number; siteId?: string };
    }
  | {
      readonly kind: 'manifest';
      readonly envelope: V1Envelope;
      readonly payload: CategoryManifest;
    }
  | {
      readonly kind: 'tombstone';
      readonly envelope: V1Envelope;
      readonly payload: Tombstone;
    }
  | {
      readonly kind: 'building_map_revision';
      readonly envelope: V1Envelope;
      readonly payload: {
        readonly site: SiteId;
        readonly level: string;
        readonly revision: string;
      };
    }
  | {
      /**
       * A task's distance totals (`TaskDistance`). Its own kind rather
       * than a live category: it has no manifest, no tombstone and no
       * reducer, and it must never reach the `tasks` reducer, whose
       * flush reads `.id.task` off every entry. `unknown` on purpose --
       * the wire is not trusted; `adaptTaskDistance` validates it.
       */
      readonly kind: 'task_distance';
      readonly envelope: V1Envelope;
      /** The task id the KEY names, which the payload must agree with. */
      readonly taskId: string;
      readonly payload: unknown;
    }
  | {
      /**
       * One line of a task's log (`rmf/v1/sites/{site}/tasks/{task}/log`,
       * a `TaskLogEntry`). Its own kind, for the reason `task_distance`
       * has one: under the `tasks` head but not a task STATE, so it must
       * never reach the `tasks` reducer. Only the open task drawer's
       * Timeline reads it (`useTaskLog`). `unknown` on purpose -- the
       * wire is not trusted; `adaptLiveTaskLogEntry` validates it.
       */
      readonly kind: 'task_log';
      readonly envelope: V1Envelope;
      /** The task id the KEY names, which the payload must agree with. */
      readonly taskId: string;
      readonly payload: unknown;
    };

/**
 * Readiness response — component-level readiness for the global API
 * (plan §3.7: "Expose component readiness separately: DB/migrations,
 * policy/site reconciliation, Zenoh command bus, and per-site worker
 * status.").
 */
export interface ReadyzResponse {
  readonly status: 'healthy' | 'degraded' | 'unavailable';
  readonly components: Readonly<Record<string, ComponentReadiness>>;
}

export interface ComponentReadiness {
  readonly status: 'healthy' | 'degraded' | 'unavailable';
  readonly detail?: string;
}

// =============================================================================
// API error envelope
// =============================================================================

export type ApiErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'site_assignment_missing'
  // Distinct from `site_assignment_missing` on purpose: both are 403s
  // meaning "you cannot have this site", but "not assigned" is fixed by
  // an administrator granting access and "retired" cannot be fixed by
  // anyone. Rendering them the same sends the user to ask for something
  // nobody can give.
  | 'site_retired'
  | 'capability_denied'
  | 'worker_unreachable'
  | 'worker_not_ready'
  | 'stale_state'
  | 'precondition_failed'
  | 'idempotency_conflict'
  // api-server 0.6.33: an operator lift verb whose key matches a record
  // older than the replay window. Refused rather than replayed, because
  // replaying it answered 200 for a press that published nothing.
  | 'idempotency_key_stale'
  | 'unsupported_capability'
  | 'invalid_request'
  | 'database_unavailable'
  | 'maintenance_mode'
  // Distinct from `capability_denied` on purpose: the caller genuinely
  // holds this capability and will hold it again the moment the alarm
  // is resolved. The UI must say "held by fire mode", not "you may not
  // do this", and must not send the user to ask for a grant.
  | 'fire_mode_active'
  // A planned maintenance window is open. Same shape as
  // `fire_mode_active` and for the same reason — the caller will hold
  // this capability again when the window closes, so the UI says "held
  // until 05:00", not "you may not do this".
  //
  // NOT `maintenance_mode` above, which is a different thing entirely:
  // the server returns that one when the fire-mode service is not
  // running in the process. Two names one letter apart; do not merge.
  | 'maintenance_window_active'
  | 'lift_not_commissioned'
  | 'lift_rule_unavailable'
  | 'lift_rule_cuts_off'
  | 'lift_rules_not_reporting'
  | 'lift_rules_off'
  | 'internal';

export interface ApiError {
  readonly code: ApiErrorCode;
  readonly message: string;
  readonly detail?: Record<string, unknown>;
}

// =============================================================================
// TanStack Query cache key factory (plan §6.2)
//
// Every cache key is site-/category-/epoch-/contract-aware so the
// REST and SSE paths can never write different representations
// of the same logical entity.
// =============================================================================

export interface QueryKeyMeta {
  readonly site: SiteId;
  readonly category:
    | 'fleets'
    | 'doors'
    | 'lifts'
    | 'robots'
    | 'tasks'
    | 'building_map'
    | 'trajectories'
    | 'manifests'
    | 'tombstones';
  readonly epoch: number | 'current';
  readonly revision?: number;
  readonly schema_version: '1.0';
}

export const v1Key = (meta: QueryKeyMeta, ...segments: ReadonlyArray<string>): readonly unknown[] =>
  [
    'v1',
    meta.site,
    meta.category,
    meta.epoch,
    meta.schema_version,
    ...(meta.revision !== undefined ? [meta.revision] : []),
    ...segments,
  ] as const;

// =============================================================================
// Lift rules (Akash, 2026-11-02): which lifts robots may use
//
// `backend/v1_contract/v1_contract/schemas.py`, and
// ai-instructions/lift-rules-design-2026-11-02.md section 8. A scope is `use`
// (boarding the car at all) or `between_floors` (riding it floor to floor).
// =============================================================================

export type LiftRuleScope = 'use' | 'between_floors';
export type LiftRuleWindowKind = 'block_use' | 'allow_travel';
export type LiftRuleSource = 'commissioning' | 'manual' | 'calendar' | 'default' | 'fire';
export type LiftRuleLaneState = 'open' | 'closed';

export interface LiftRuleHoldRequest {
  /** `released`, `next_change`, or an epoch-ms instant. */
  readonly until: 'released' | 'next_change' | number;
  readonly reason?: string | null;
  /** True once the supervisor has read which floors lose their way back to a
   *  charger. */
  readonly confirm_cut_off?: boolean;
}

export interface LiftRuleHold {
  readonly until_kind: 'time' | 'released' | 'next_change';
  /** The instant it ends, whichever kind. Null: until released, or no calendar
   *  change is coming. */
  readonly until_ms?: number | null;
  readonly reason?: string | null;
  readonly set_by_email?: string | null;
  readonly set_at_ms: number;
}

/** A robot a change is waiting on. `lift` may be another lift: a plan can hold
 *  lanes of a lift further on, so a close waits for a robot on board any. */
export interface LiftRuleHoldingRobot {
  readonly robot?: string;
  readonly lift?: string;
  readonly phase?: string;
  readonly destination_level?: string;
  readonly since_ms?: number;
}

export interface LiftRuleScopeView {
  readonly scope: LiftRuleScope;
  /** False when the built map has no lanes for this scope. */
  readonly available: boolean;
  readonly desired: LiftRuleLaneState;
  readonly source: LiftRuleSource;
  /** What the fleet adapters have actually applied. The console shows this,
   *  never just what was asked. */
  readonly applied: LiftRuleLaneState | 'unknown';
  readonly hold?: LiftRuleHold | null;
  readonly window_id?: string | null;
  readonly reason?: string | null;
  readonly next_change_ms?: number | null;
  readonly next_desired?: LiftRuleLaneState | null;
  readonly pending?: boolean;
  readonly pending_reason?: string | null;
  readonly pending_since_ms?: number | null;
  readonly holding_robots?: readonly LiftRuleHoldingRobot[];
  /** A lift refusal is holding the car closed, whatever staff asked. */
  readonly refused?: boolean;
  readonly stuck?: boolean;
  readonly unconfirmed?: boolean;
  readonly breaker?: string;
  /** Floors that would lose their way back to a charger if this closed. */
  readonly would_cut_off?: readonly string[];
}

export interface LiftRuleLiftView {
  readonly lift: string;
  readonly commissioned: boolean;
  /** `entry`, `move`, `exit`, `levels`, `two_sided_levels`. */
  readonly graph: Readonly<Record<string, number>>;
  readonly use: LiftRuleScopeView;
  readonly between_floors: LiftRuleScopeView;
}

export interface LiftRulesDelivery {
  readonly state: 'delivered' | 'sending' | 'failed';
  readonly failed_since_ms?: number | null;
  readonly last_sent_ms?: number | null;
}

export interface LiftRulesView {
  readonly site: string;
  /** The site's admin switch. False: the console draws nothing about lift rules.
   *  Absent reads as on: an api-server from before the switch. */
  readonly enabled?: boolean;
  readonly fire_active: boolean;
  readonly delivery: LiftRulesDelivery;
  readonly lifts: readonly LiftRuleLiftView[];
}

export interface CreateLiftRuleWindowRequest {
  readonly lift: string;
  readonly kind: LiftRuleWindowKind;
  readonly starts_at_ms: number;
  readonly duration_ms: number;
  /** An RRULE body, or null for one occurrence, in `timezone`. */
  readonly rrule?: string | null;
  readonly timezone: string;
  readonly repeat_until_ms?: number | null;
  readonly reason?: string | null;
  readonly confirm_cut_off?: boolean;
}

export interface LiftRuleWindow {
  readonly window_id: string;
  readonly site: string;
  readonly lift: string;
  readonly kind: LiftRuleWindowKind;
  readonly starts_at_ms: number;
  readonly duration_ms: number;
  readonly rrule?: string | null;
  readonly timezone: string;
  readonly repeat_until_ms?: number | null;
  readonly reason?: string | null;
  readonly enabled: boolean;
  readonly exdates_ms: readonly number[];
  readonly created_by_sub: string;
  readonly created_by_email?: string | null;
  readonly created_at_ms: number;
  readonly updated_at_ms: number;
}

/** One run of a window, for the calendar's bands. */
export interface LiftRuleOccurrence {
  readonly window_id: string;
  readonly lift: string;
  readonly kind: LiftRuleWindowKind;
  readonly starts_at_ms: number;
  readonly ends_at_ms: number;
  readonly reason?: string | null;
}

export interface LiftRuleOccurrencePage {
  readonly occurrences: readonly LiftRuleOccurrence[];
  readonly truncated: boolean;
}
